import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { ChevronDown, RefreshCw, ArrowDown, ArrowUp, Calendar, Check, FileDown } from 'lucide-react';

/**
 * English copy of HupalupaZooFamilyRaporu for the global Zoo Family team (/r/hupalupa-zoo-family-en).
 * Same endpoint and layout; only copy and number formatting differ. Turkish original stays at /r/hupalupa-zoo-family.
 *
 * HUPALUPA Store · The Zoo Family reklam raporu.
 *
 * Overview2 "Marka Bazlı Harcama ve Ciro" tablosundaki Zoo Family satırının açılmış hâli:
 *   1. Özet           harcama, gösterim, tıklama, CTR, platform dönüşümü / ROAS
 *   2. Platformlar    Google · Meta · TikTok
 *   3. Aylık          harcama + GA4 Zoo Family cirosu
 *   4. Kampanyalar    Zoo Family'ye ayrılmış reklam grubu / varlık grubu / adset
 *   5. Ürünler        genel katalog kampanyalarında gösterilen Zoo Family ürünleri
 *   6. Satış          GA4'te Zoo Family ürünleri (reklam atfı DEĞİL)
 * Uç: GET /api/hupalupa/zoo-family (glowytics-backend/src/routes/hupalupaZooFamily.js).
 * Sadece HUPALUPA Store (App.js allowBrandIds). PDF: Europcar Kaynak Raporu'ndaki iframe + print yöntemi.
 */

const API_BASE = (() => {
  const raw = (process.env.REACT_APP_API_URL || 'https://api.glowytics.com').trim();
  return raw.replace(/\/+$/, '').replace(/\/api$/, '');
})();

const C = {
  ground: '#F6F8FF', card: '#FFFFFF', line: '#E5E7EB', hair: '#F3F4F6',
  ink: '#111827', ink2: '#374151', muted: '#6B7280', faint: '#9CA3AF',
  accent: '#436AEB', accentSoft: '#EEF2FE', up: '#047857', down: '#B91C1C'
};
const PLATFORM = {
  google: { label: 'Google Ads', renk: '#436AEB' },
  meta: { label: 'Meta Ads', renk: '#eb6834' },
  tiktok: { label: 'TikTok Ads', renk: '#1baf7a' }
};
const PLATFORMLAR = ['google', 'meta', 'tiktok'];

const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
const nf = (n) => Math.round(Number(n || 0)).toLocaleString('en-US');
const nf1 = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 1 });
const tl = (n) => `₺${nf(n)}`;
const tl2 = (n) => `₺${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const tlShort = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e6) return `₺${(v / 1e6).toLocaleString('en-US', { maximumFractionDigits: 1 })}M`;
  if (Math.abs(v) >= 1e4) return `₺${(v / 1e3).toLocaleString('en-US', { maximumFractionDigits: 0 })}K`;
  return tl(v);
};
const sayiKisa = (n) => {
  const v = Number(n || 0);
  if (v >= 1e6) return `${(v / 1e6).toLocaleString('en-US', { maximumFractionDigits: 2 })}M`;
  if (v >= 1e4) return `${(v / 1e3).toLocaleString('en-US', { maximumFractionDigits: 0 })}K`;
  return nf(v);
};
const pct = (x, d = 0) => `${(Number(x || 0) * 100).toLocaleString('en-US', { maximumFractionDigits: d, minimumFractionDigits: d })}%`;
const oran = (a, b) => (b > 0 ? a / b : null);
const ctrYazi = (v) => (v == null ? '—' : pct(v, 2));
const roasYazi = (v) => (v == null || !Number.isFinite(v) ? '—' : `${v.toLocaleString('en-US', { maximumFractionDigits: 2 })}×`);
const AY = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const AY_TAM = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const gunEtiket = (iso) => `${AY[Number(iso.slice(5, 7)) - 1]} ${Number(iso.slice(8, 10))}`;
const aralikEtiket = (a, b) => `${gunEtiket(a)}${a.slice(0, 4) !== b.slice(0, 4) ? ` ${a.slice(0, 4)}` : ''} – ${gunEtiket(b)} ${b.slice(0, 4)}`;
const ayTam = (ym) => `${AY_TAM[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}`;

const iso = (d) => {
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
};

/* ---------------------------------------------------------------- tarih aralığı */

const ARALIKLAR = [
  { key: 'buyil', label: 'Year to date' },
  { key: 'buay', label: 'This month' },
  { key: 'gecenay', label: 'Last month' },
  { key: '30', label: 'Last 30 days' },
  { key: '3ay', label: 'Last 3 months' },
  { key: '6ay', label: 'Last 6 months' },
  { key: '12ay', label: 'Last 12 months' },
  { key: 'ozel', label: 'Custom range' }
];
function aralikOf(key) {
  const dun = new Date(); dun.setDate(dun.getDate() - 1);
  const ayBasi = (geri) => iso(new Date(dun.getFullYear(), dun.getMonth() - geri, 1));
  if (key === 'buay') return [ayBasi(0), iso(dun)];
  if (key === 'gecenay') {
    const b = new Date(); b.setDate(1); b.setMonth(b.getMonth() - 1);
    const s = new Date(); s.setDate(0);
    return [iso(b), iso(s)];
  }
  if (key === '30') { const d = new Date(dun); d.setDate(d.getDate() - 29); return [iso(d), iso(dun)]; }
  if (key === '3ay') return [ayBasi(2), iso(dun)];
  if (key === '6ay') return [ayBasi(5), iso(dun)];
  if (key === 'buyil') return [`${dun.getFullYear()}-01-01`, iso(dun)];
  if (key === '12ay') return [ayBasi(11), iso(dun)];
  return null;
}

/* ---------------------------------------------------------------- parçacıklar */

function Card({ children, style, className = '', id }) {
  return (
    <div id={id} className={className}
      style={{ background: C.card, borderRadius: 18, border: `1px solid ${C.hair}`, boxShadow: '0 1px 2px rgba(17,24,39,.04), 0 8px 24px rgba(17,24,39,.04)', ...style }}>
      {children}
    </div>
  );
}

function BolumBaslik({ id, eyebrow, title, sub, right }) {
  return (
    <div id={id} className="pdf-bolum" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap', marginTop: 52, marginBottom: 16 }}>
      <div style={{ minWidth: 0 }}>
        {eyebrow ? <div style={{ fontSize: 13, fontWeight: 600, color: C.accent }}>{eyebrow}</div> : null}
        <h2 style={{ fontSize: 28, fontWeight: 700, letterSpacing: '-0.022em', margin: '2px 0 0', lineHeight: 1.15, color: C.ink }}>{title}</h2>
        {sub ? <div style={{ fontSize: 15, color: C.muted, marginTop: 6, maxWidth: 820, lineHeight: 1.45 }}>{sub}</div> : null}
      </div>
      {right}
    </div>
  );
}

function Kpi({ label, value, lines, renk }) {
  return (
    <Card style={{ padding: '20px 20px 18px', position: 'relative', overflow: 'hidden' }}>
      {renk ? <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: renk }} /> : null}
      <div style={{ fontSize: 13, fontWeight: 600, color: C.muted }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 700, letterSpacing: '-0.025em', color: C.ink, marginTop: 8, lineHeight: 1.05 }} className="tabular-nums">
        {value}
      </div>
      <div style={{ marginTop: 10, display: 'grid', gap: 3 }}>
        {lines.filter(Boolean).map((l) => (
          <div key={l[0]} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 13, color: C.muted }} className="tabular-nums">
            <span>{l[0]}</span><span style={{ color: C.ink2, fontWeight: 600, whiteSpace: 'nowrap' }}>{l[1]}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function PlatformRozet({ p }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color: C.ink2, whiteSpace: 'nowrap' }}>
      <span style={{ width: 8, height: 8, borderRadius: 4, background: PLATFORM[p].renk }} />
      {PLATFORM[p].label}
    </span>
  );
}

const th = { padding: '10px 10px', fontWeight: 500, fontSize: 12, color: C.muted, textAlign: 'right', whiteSpace: 'nowrap', borderBottom: `1px solid ${C.line}` };
const td = { padding: '11px 10px', textAlign: 'right', whiteSpace: 'nowrap', borderTop: `1px solid ${C.hair}`, color: C.ink2 };
const tdTop = { ...td, fontWeight: 700, color: C.ink, borderTop: `2px solid ${C.line}`, background: '#FAFBFF' };

/** Ortak metrik sütunları — her tabloda aynı sırayla. */
const METRIK_KOLON = [
  { key: 'cost', label: 'Spend', fmt: tl },
  { key: 'impressions', label: 'Impressions', fmt: nf },
  { key: 'clicks', label: 'Clicks', fmt: nf },
  { key: 'ctr', label: 'CTR', fmt: ctrYazi },
  { key: 'cpc', label: 'CPC', fmt: (v) => (v == null ? '—' : tl2(v)) },
  { key: 'conv', label: 'Conversions', fmt: (v) => nf1(v) },
  { key: 'value', label: 'Conv. value', fmt: tl },
  { key: 'roas', label: 'ROAS', fmt: roasYazi }
];
const turet = (x) => ({
  ...x,
  ctr: oran(x.clicks, x.impressions),
  cpc: oran(x.cost, x.clicks),
  roas: oran(x.value, x.cost)
});
const topla = (rows) => rows.reduce((a, r) => ({
  cost: a.cost + (r.cost || 0), impressions: a.impressions + (r.impressions || 0), clicks: a.clicks + (r.clicks || 0),
  conv: a.conv + (r.conv || 0), value: a.value + (r.value || 0)
}), { cost: 0, impressions: 0, clicks: 0, conv: 0, value: 0 });

function SiraliBaslik({ kolonlar, sira, setSira, ilk, ilkGenislik }) {
  const sirala = (key) => setSira((s) => (s.key === key ? { key, desc: !s.desc } : { key, desc: true }));
  return (
    <thead>
      <tr>
        {ilk.map((h, i) => (
          <th key={h} style={{ ...th, textAlign: 'left', paddingLeft: i === 0 ? 24 : 10, width: i === 0 ? ilkGenislik : undefined }}>{h}</th>
        ))}
        {kolonlar.map((c, i) => (
          <th key={c.key} onClick={() => sirala(c.key)}
            style={{ ...th, cursor: 'pointer', userSelect: 'none', color: sira.key === c.key ? C.ink : C.muted, fontWeight: sira.key === c.key ? 600 : 500, paddingRight: i === kolonlar.length - 1 ? 24 : 10 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              {c.label}
              {sira.key === c.key ? (sira.desc ? <ArrowDown size={12} /> : <ArrowUp size={12} />) : null}
            </span>
          </th>
        ))}
      </tr>
    </thead>
  );
}

const siraUygula = (rows, sira) => [...rows].sort((a, b) => {
  const av = a[sira.key] ?? -Infinity; const bv = b[sira.key] ?? -Infinity;
  return (sira.desc ? bv - av : av - bv) || b.cost - a.cost;
});

function Bos({ loading, yazi }) {
  return <div style={{ padding: 24, fontSize: 14, color: C.muted }}>{loading ? 'Loading…' : yazi}</div>;
}

function Dipnot({ children }) {
  return <div style={{ fontSize: 12, color: C.faint, padding: '12px 24px 10px', lineHeight: 1.6 }}>{children}</div>;
}

/* ---------------------------------------------------------------- platformlar */

function PlatformTablosu({ data }) {
  const rows = PLATFORMLAR.map((p) => turet({ p, ...(data?.platforms?.[p] || {}) })).filter((r) => r.cost > 0 || r.impressions > 0);
  const top = turet(topla(rows));
  return (
    <Card style={{ padding: '8px 0 4px' }}>
      <div className="overflow-x-auto">
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 14, minWidth: 980 }} className="tabular-nums">
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left', paddingLeft: 24 }}>Platform</th>
              <th style={{ ...th, textAlign: 'left' }}>Share of spend</th>
              {METRIK_KOLON.map((c, i) => <th key={c.key} style={{ ...th, paddingRight: i === METRIK_KOLON.length - 1 ? 24 : 10 }}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.p}>
                <td style={{ ...td, textAlign: 'left', paddingLeft: 24 }}><PlatformRozet p={r.p} /></td>
                <td style={{ ...td, textAlign: 'left' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 90, height: 6, background: C.hair, borderRadius: 3 }}>
                      <div style={{ width: `${top.cost ? (r.cost / top.cost) * 100 : 0}%`, height: 6, borderRadius: 3, background: PLATFORM[r.p].renk }} />
                    </div>
                    <span style={{ minWidth: 34 }}>{top.cost ? pct(r.cost / top.cost) : '—'}</span>
                  </div>
                </td>
                {METRIK_KOLON.map((c, i) => (
                  <td key={c.key} style={{ ...td, paddingRight: i === METRIK_KOLON.length - 1 ? 24 : 10, fontWeight: c.key === 'cost' || c.key === 'roas' ? 600 : 400, color: c.key === 'cost' || c.key === 'roas' ? C.ink : C.ink2 }}>
                    {c.fmt(r[c.key])}
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td style={{ ...tdTop, textAlign: 'left', paddingLeft: 24 }}>Total</td>
              <td style={{ ...tdTop, textAlign: 'left' }}>100%</td>
              {METRIK_KOLON.map((c, i) => <td key={c.key} style={{ ...tdTop, paddingRight: i === METRIK_KOLON.length - 1 ? 24 : 10 }}>{c.fmt(top[c.key])}</td>)}
            </tr>
          </tbody>
        </table>
      </div>
      <Dipnot>
        Conversions and conversion value are purchases as reported by each platform; ROAS = conversion value / spend.
        TikTok ran only as a traffic campaign (purchases not measured) and its "click" definition is broad, so its CTR is not comparable to Google/Meta.
      </Dipnot>
    </Card>
  );
}

/* ---------------------------------------------------------------- aylık */

function AylikGrafik({ months }) {
  const max = Math.max(1, ...months.map((m) => m.total.cost));
  const H = 180;
  return (
    <div className="pdf-blok" style={{ padding: '22px 24px 8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: C.ink }}>Monthly spend</div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {PLATFORMLAR.map((p) => <PlatformRozet key={p} p={p} />)}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: H + 46, marginTop: 14 }}>
        {months.map((m) => (
          <div key={m.month} style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: C.ink2, marginBottom: 6, whiteSpace: 'nowrap' }} className="tabular-nums">
              {m.total.cost ? tlShort(m.total.cost) : ''}
            </div>
            <div style={{ width: '100%', maxWidth: 64, display: 'flex', flexDirection: 'column-reverse', borderRadius: 6, overflow: 'hidden', height: (m.total.cost / max) * H, minHeight: m.total.cost ? 2 : 0 }}>
              {PLATFORMLAR.map((p) => (m[p].cost > 0 ? (
                <div key={p} title={`${PLATFORM[p].label}: ${tl(m[p].cost)}`} style={{ height: `${(m[p].cost / m.total.cost) * 100}%`, background: PLATFORM[p].renk }} />
              ) : null))}
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>{AY[Number(m.month.slice(5, 7)) - 1]}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AylikTablo({ data, loading }) {
  const months = data?.months || [];
  if (!months.length) return <Card><Bos loading={loading} yazi="No data for this period." /></Card>;
  const satirlar = months.map((m) => ({ ...turet(m.total), month: m.month, ga4Revenue: m.ga4Revenue || 0, ga4Qty: m.ga4Qty || 0, by: m }));
  const top = { ...turet(topla(satirlar.map((s) => s))), ga4Revenue: satirlar.reduce((a, s) => a + s.ga4Revenue, 0), ga4Qty: satirlar.reduce((a, s) => a + s.ga4Qty, 0) };
  const kolon = [
    ...PLATFORMLAR.map((p) => ({ key: p, label: PLATFORM[p].label, v: (s) => s.by?.[p]?.cost, fmt: (v) => (v ? tl(v) : '—'), renk: PLATFORM[p].renk })),
    ...METRIK_KOLON.map((c) => ({ key: c.key, label: c.key === 'cost' ? 'Total spend' : c.label, v: (s) => s[c.key], fmt: c.fmt, kalin: c.key === 'cost' || c.key === 'roas' })),
    { key: 'ga4', label: 'GA4 Zoo Family revenue', v: (s) => s.ga4Revenue, fmt: (v) => (v ? tl(v) : '—'), ayri: true },
    { key: 'ga4q', label: 'Units', v: (s) => s.ga4Qty, fmt: (v) => (v ? nf(v) : '—') }
  ];
  const topDeger = (k) => (k.key === 'ga4' ? top.ga4Revenue : k.key === 'ga4q' ? top.ga4Qty : PLATFORMLAR.includes(k.key) ? data.platforms[k.key].cost : top[k.key]);
  return (
    <Card style={{ padding: 0, opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }}>
      <AylikGrafik months={months} />
      <div className="overflow-x-auto" style={{ marginTop: 6 }}>
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13.5, minWidth: 1400 }} className="tabular-nums">
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left', paddingLeft: 24 }}>Month</th>
              {kolon.map((k, i) => (
                <th key={k.key} style={{ ...th, paddingRight: i === kolon.length - 1 ? 24 : 10, borderLeft: k.ayri ? `1px solid ${C.line}` : undefined }}>
                  {k.renk ? <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: 4, background: k.renk, marginRight: 5 }} /> : null}{k.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {satirlar.map((s) => (
              <tr key={s.month}>
                <td style={{ ...td, textAlign: 'left', paddingLeft: 24, fontWeight: 600, color: C.ink }}>{ayTam(s.month)}</td>
                {kolon.map((k, i) => (
                  <td key={k.key} style={{ ...td, paddingRight: i === kolon.length - 1 ? 24 : 10, fontWeight: k.kalin ? 600 : 400, color: k.kalin ? C.ink : C.ink2, borderLeft: k.ayri ? `1px solid ${C.line}` : undefined }}>
                    {k.fmt(k.v(s))}
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td style={{ ...tdTop, textAlign: 'left', paddingLeft: 24 }}>Total</td>
              {kolon.map((k, i) => (
                <td key={k.key} style={{ ...tdTop, paddingRight: i === kolon.length - 1 ? 24 : 10, borderLeft: k.ayri ? `1px solid ${C.line}` : undefined }}>{k.fmt(topDeger(k))}</td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <Dipnot>
        GA4 revenue is every Zoo Family product the site sold that month (GA4 itemBrand); it is not ad-attributed and is not part of ROAS.
      </Dipnot>
    </Card>
  );
}

/* ---------------------------------------------------------------- kampanyalar */

function KampanyaTablosu({ data, loading }) {
  const [sira, setSira] = useState({ key: 'cost', desc: true });
  const rows = useMemo(() => siraUygula((data?.nodes || []).map(turet), sira), [data, sira]);
  if (!rows.length) return <Card><Bos loading={loading} yazi="No Zoo Family-dedicated campaigns in this period." /></Card>;
  const top = turet(topla(rows));
  return (
    <Card style={{ padding: '8px 0 4px', opacity: loading ? 0.55 : 1 }}>
      <div className="overflow-x-auto">
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13.5, minWidth: 1360 }} className="tabular-nums">
          <SiraliBaslik kolonlar={METRIK_KOLON} sira={sira} setSira={setSira} ilk={['Campaign › ad group', 'Flight']} ilkGenislik={400} />
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.platform}|${r.campaign}|${r.node}`}>
                <td style={{ ...td, textAlign: 'left', paddingLeft: 24, whiteSpace: 'normal' }}>
                  <div style={{ width: 400 }}>
                    <div style={{ fontWeight: 600, color: C.ink, lineHeight: 1.35, wordBreak: 'break-word' }}>{r.node}</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 3, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <PlatformRozet p={r.platform} />
                      <span>{r.tip}</span>
                      <span style={{ color: C.faint }}>· {r.campaign}</span>
                    </div>
                  </div>
                </td>
                <td style={{ ...td, textAlign: 'left', fontSize: 12.5, color: C.muted }}>{aralikEtiket(r.first, r.last)}</td>
                {METRIK_KOLON.map((c, i) => (
                  <td key={c.key} style={{ ...td, paddingRight: i === METRIK_KOLON.length - 1 ? 24 : 10, fontWeight: c.key === 'cost' || c.key === 'roas' ? 600 : 400, color: c.key === 'cost' || c.key === 'roas' ? C.ink : C.ink2 }}>
                    {c.fmt(r[c.key])}
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td style={{ ...tdTop, textAlign: 'left', paddingLeft: 24 }}>Total <span style={{ color: C.faint, fontWeight: 500, fontSize: 12.5 }}>· {rows.length} rows</span></td>
              <td style={tdTop} />
              {METRIK_KOLON.map((c, i) => <td key={c.key} style={{ ...tdTop, paddingRight: i === METRIK_KOLON.length - 1 ? 24 : 10 }}>{c.fmt(top[c.key])}</td>)}
            </tr>
          </tbody>
        </table>
      </div>
      <Dipnot>
        Spend is read at the most granular level: Google ad group and PMax asset group, Meta ad set, TikTok ad group. If that name doesn't mention Zoo Family,
        the campaign name is used (e.g. Glowy_Search_ZooFamily › Ad group 1). Same rule and same total as the brand table in the main dashboard.
        {data?.excluded?.length ? (
          <> Named Zoo Family but assigned to another brand: {data.excluded.map((e) => `${e.campaign} › ${e.node} (${tl(e.cost)}, ${e.brand || 'no brand'})`).join('; ')} — the ad itself promotes that brand.</>
        ) : null}
      </Dipnot>
    </Card>
  );
}

/* ---------------------------------------------------------------- ürünler */

const URUN_KOLON = METRIK_KOLON.filter((c) => c.key !== 'cpc');

function UrunTablosu({ data, loading }) {
  const [sira, setSira] = useState({ key: 'cost', desc: true });
  const [hepsi, setHepsi] = useState(false);
  const pr = data?.products;
  const rows = useMemo(() => siraUygula((pr?.items || []).map(turet), sira), [pr, sira]);
  if (!rows.length) {
    return <Card><Bos loading={loading} yazi={pr?.googleError ? `Google product data unavailable: ${pr.googleError}` : 'No product data for this period.'} /></Card>;
  }
  const top = turet(topla(rows));
  const gorunen = hepsi ? rows : rows.slice(0, 20);
  return (
    <Card style={{ padding: '8px 0 4px', opacity: loading ? 0.55 : 1 }}>
      <div className="overflow-x-auto">
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13.5, minWidth: 1200 }} className="tabular-nums">
          <SiraliBaslik kolonlar={URUN_KOLON} sira={sira} setSira={setSira} ilk={['Product']} ilkGenislik={460} />
          <tbody>
            {gorunen.map((r) => (
              <tr key={`${r.platform}|${r.itemId}`}>
                <td style={{ ...td, textAlign: 'left', paddingLeft: 24, whiteSpace: 'normal' }}>
                  <div style={{ width: 460 }}>
                    <div style={{ fontWeight: 600, color: C.ink, lineHeight: 1.35 }}>{r.title || r.itemId}</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 3, display: 'flex', gap: 8, alignItems: 'center' }}>
                      <PlatformRozet p={r.platform} />
                      {r.extraCost > 0 ? <span>· {tl(r.extraCost)} in general campaigns</span> : null}
                    </div>
                  </div>
                </td>
                {URUN_KOLON.map((c, i) => (
                  <td key={c.key} style={{ ...td, paddingRight: i === URUN_KOLON.length - 1 ? 24 : 10, fontWeight: c.key === 'cost' ? 600 : 400, color: c.key === 'cost' ? C.ink : C.ink2 }}>
                    {r.platform === 'meta' && (c.key === 'conv' || c.key === 'value' || c.key === 'roas') ? '—' : c.fmt(r[c.key])}
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td style={{ ...tdTop, textAlign: 'left', paddingLeft: 24 }}>Total <span style={{ color: C.faint, fontWeight: 500, fontSize: 12.5 }}>· {rows.length} products</span></td>
              {URUN_KOLON.map((c, i) => <td key={c.key} style={{ ...tdTop, paddingRight: i === URUN_KOLON.length - 1 ? 24 : 10 }}>{c.fmt(top[c.key])}</td>)}
            </tr>
          </tbody>
        </table>
      </div>
      {rows.length > 20 ? (
        <div className="pdf-gizle" style={{ padding: '10px 24px 0' }}>
          <button type="button" onClick={() => setHepsi((v) => !v)}
            style={{ border: 0, background: 'transparent', color: C.accent, fontWeight: 600, fontSize: 13.5, cursor: 'pointer', padding: 0 }}>
            {hepsi ? 'Show less' : `Show all ${rows.length} products`}
          </button>
        </div>
      ) : null}
      <Dipnot>
        Google: shopping product report (PMax shopping share + Standard Shopping), live Google Ads API. Meta: product breakdown of catalog ads
        (recorded since June 9, 2026); Meta does not report purchases at product level, so conversion columns are empty.
        Shopping impressions of the PMax "Zoo Family Kamera" asset group also appear here, so the product total is not added to the campaign total above.
        Only the share in general campaigns not dedicated to Zoo Family ("in general campaigns") is additional spend.
        {pr?.googleError ? ` Google product data unavailable: ${pr.googleError}` : ''}
      </Dipnot>
    </Card>
  );
}

/* ---------------------------------------------------------------- GA4 satış */

function SatisTablosu({ data, loading }) {
  const items = data?.ga4?.items || [];
  if (!items.length) return <Card><Bos loading={loading} yazi={data?.ga4Error ? `GA4 unavailable: ${data.ga4Error}` : 'No Zoo Family sales in this period.'} /></Card>;
  const top = items.reduce((a, r) => ({ views: a.views + r.views, carts: a.carts + r.carts, qty: a.qty + r.qty, revenue: a.revenue + r.revenue }), { views: 0, carts: 0, qty: 0, revenue: 0 });
  const max = Math.max(1, ...items.map((r) => r.revenue));
  const gorunen = items.slice(0, 15);
  const kalan = items.slice(15);
  const kolonlar = [
    ['views', 'Product views', nf], ['carts', 'Add to cart', nf], ['sepetOrani', 'Add-to-cart rate', (v) => (v == null ? '—' : pct(v, 1))],
    ['qty', 'Units sold', nf], ['revenue', 'Revenue', tl], ['pay', 'Revenue share', (v) => (v == null ? '—' : pct(v, 1))]
  ];
  const tur = (r) => ({ ...r, sepetOrani: oran(r.carts, r.views), pay: oran(r.revenue, top.revenue) });
  return (
    <Card style={{ padding: '8px 0 4px', opacity: loading ? 0.55 : 1 }}>
      <div className="overflow-x-auto">
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13.5, minWidth: 1100 }} className="tabular-nums">
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left', paddingLeft: 24 }}>Product</th>
              {kolonlar.map(([k, l], i) => <th key={k} style={{ ...th, paddingRight: i === kolonlar.length - 1 ? 24 : 10 }}>{l}</th>)}
            </tr>
          </thead>
          <tbody>
            {[...gorunen, ...(kalan.length ? [{ name: `Other ${kalan.length} products`, diger: true, ...kalan.reduce((a, r) => ({ views: a.views + r.views, carts: a.carts + r.carts, qty: a.qty + r.qty, revenue: a.revenue + r.revenue }), { views: 0, carts: 0, qty: 0, revenue: 0 }) }] : [])].map(tur).map((r) => (
              <tr key={r.name}>
                <td style={{ ...td, textAlign: 'left', paddingLeft: 24, whiteSpace: 'normal' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: 520 }}>
                    <span style={{ flex: 1, fontWeight: r.diger ? 500 : 600, color: r.diger ? C.muted : C.ink, lineHeight: 1.35 }}>{r.name}</span>
                    <div style={{ width: 70, height: 6, background: C.hair, borderRadius: 3, flexShrink: 0 }}>
                      <div style={{ width: `${(r.revenue / max) * 100}%`, maxWidth: '100%', height: 6, borderRadius: 3, background: C.accent }} />
                    </div>
                  </div>
                </td>
                {kolonlar.map(([k, , f], i) => (
                  <td key={k} style={{ ...td, paddingRight: i === kolonlar.length - 1 ? 24 : 10, fontWeight: k === 'revenue' ? 600 : 400, color: k === 'revenue' ? C.ink : C.ink2 }}>{f(r[k])}</td>
                ))}
              </tr>
            ))}
            <tr>
              <td style={{ ...tdTop, textAlign: 'left', paddingLeft: 24 }}>Total <span style={{ color: C.faint, fontWeight: 500, fontSize: 12.5 }}>· {items.length} products</span></td>
              {kolonlar.map(([k, , f], i) => (
                <td key={k} style={{ ...tdTop, paddingRight: i === kolonlar.length - 1 ? 24 : 10 }}>{f(tur(top)[k])}</td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <Dipnot>
        GA4 e-commerce (itemBrand "The Zoo Family"): every Zoo Family product the site sold in this period, from any channel. Not ad-attributed.
      </Dipnot>
    </Card>
  );
}

/* ---------------------------------------------------------------- product range (static) */

/**
 * Zoo Family product range at HUPALUPA Store, snapshot Oct 27, 2026 (public/zoo-family-assets/catalog.json).
 * Range/price/stock: hupalupa.store/zoo-family; P2 Bear + Fox are delisted, taken from Merchant Center history.
 * Sales: GA4 itemBrand "Zoo Family", merged by itemId; revenue incl. VAT, returns not deducted.
 * ytd = Jan 1 – Oct 26, 2026 · l12 = Nov 1, 2025 – Oct 26, 2026. Each row: [nameEn, image, price, inStock, stockQty, ytdQty, ytdRev, l12Qty, l12Rev]
 */
const RANGE = [
  { key: 'p2', label: 'P2 instant-print camera', items: [
    ["P2 Unicorn", "catalog/31238.jpg", 6600, true, 4, 39, 230976, 49, 275366],
    ["P2 Dino Green", "catalog/31239.jpg", 6600, true, 1, 13, 81558, 20, 114850],
    ["P2 Bear", "catalog/31794.jpg", 6600, false, null, 14, 82938, 15, 88728],
    ["P2 Fox", "catalog/31793.jpg", 6600, false, null, 4, 23298, 4, 23298],
  ] },
  { key: 'c5', label: 'C5 camera', items: [
    ["C5 Unicorn", "catalog/31235.jpg", 4950, false, 0, 18, 80536, 22, 97862],
    ["C5 Dino Green", "catalog/31237.jpg", 4950, true, 1, 9, 42075, 9, 42075],
    ["C5 Bear", "catalog/31236.jpg", 4950, false, 0, 3, 13464, 6, 24602],
    ["C5 Fox", "catalog/31234.jpg", 4950, false, 0, 1, 4950, 2, 8662],
  ] },
  { key: 'c1', label: 'C1 camera', items: [
    ["C1 Fox", "catalog/31230.jpg", 4390, false, 0, 1, 4390, 5, 19755],
    ["C1 Giraffe", "catalog/31232.jpg", 4390, false, 0, 2, 8780, 2, 8780],
    ["C1 Penguin", "catalog/31231.jpg", 4390, false, 0, 1, 4390, 1, 4390],
    ["C1 Rabbit Flower", "catalog/31233.jpg", 4390, false, 0, 0, 0, 1, 3658],
    ["C1 Unicorn", "catalog/31229.jpg", 4390, false, 0, 0, 0, 1, 3292],
  ] },
  { key: 'bag', label: 'Silicone bag', items: [
    ["Silicone bag Unicorn", "catalog/31241.jpg", 1250, false, 0, 22, 24819, 26, 28569],
    ["Silicone bag Dino Green", "catalog/31243.jpg", 1250, true, 3, 12, 13874, 15, 16999],
    ["Silicone bag Bear", "catalog/31795.jpg", 1250, true, 1, 8, 8733, 9, 9670],
    ["Silicone bag Fox", "catalog/31240.jpg", 1250, true, 4, 4, 4712, 5, 5650],
    ["Silicone bag Rabbit Flower", "catalog/31242.jpg", 1250, false, 0, 2, 2212, 2, 2212],
  ] },
  { key: 'paper', label: 'Paper rolls', items: [
    ["Adhesive paper rolls (3-pack)", "catalog/31245.jpg", 890, true, 54, 34, 27402, 44, 32031],
    ["Paper rolls (3-pack)", "catalog/31244.jpg", 790, true, 80, 25, 17718, 29, 19270],
  ] },
  { key: 'light', label: 'Night light (Zoo Light)', items: [
    ["Zoo Light Dino", "catalog/32765.jpg", 2250, true, 6, 8, 17415, 9, 19665],
    ["Zoo Light Unicorn", "catalog/32763.jpg", 2250, false, 0, 6, 11520, 6, 11520],
    ["Zoo Light Fox", "catalog/32764.jpg", 2250, true, 3, 1, 2250, 2, 4500],
  ] },
];

function Urunler() {
  const kat = RANGE.map((c) => {
    const items = c.items.map(([name, image, price, inStock, stockQty, yq, yr, lq, lr]) => ({ name, image, price, inStock, stockQty, yq, yr, lq, lr }));
    const sum = (k) => items.reduce((a, x) => a + x[k], 0);
    return { ...c, items, n: items.length, stokta: items.filter((x) => x.inStock).length, yq: sum('yq'), yr: sum('yr'), lq: sum('lq'), lr: sum('lr') };
  });
  const top = { n: kat.reduce((a, c) => a + c.n, 0), yq: kat.reduce((a, c) => a + c.yq, 0), yr: kat.reduce((a, c) => a + c.yr, 0), lq: kat.reduce((a, c) => a + c.lq, 0), lr: kat.reduce((a, c) => a + c.lr, 0) };
  return (
    <>
      <Card style={{ padding: '8px 0 4px' }}>
        <div className="overflow-x-auto">
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 14, minWidth: 900 }} className="tabular-nums">
            <thead>
              <tr>
                <th style={{ ...th, textAlign: 'left', paddingLeft: 24 }}>Category</th>
                <th style={th}>Products</th><th style={th}>In stock</th><th style={th}>Price</th>
                <th style={{ ...th, borderLeft: `1px solid ${C.line}` }}>Units 2026 YTD</th><th style={th}>Revenue 2026 YTD</th><th style={th}>Share</th>
                <th style={{ ...th, borderLeft: `1px solid ${C.line}` }}>Units last 12 mo.</th><th style={{ ...th, paddingRight: 24 }}>Revenue last 12 mo.</th>
              </tr>
            </thead>
            <tbody>
              {kat.map((c) => (
                <tr key={c.key}>
                  <td style={{ ...td, textAlign: 'left', paddingLeft: 24, fontWeight: 600, color: C.ink }}>{c.label}</td>
                  <td style={td}>{c.n}</td>
                  <td style={td}>{c.stokta}</td>
                  <td style={td}>{tl(c.items[0].price)}</td>
                  <td style={{ ...td, borderLeft: `1px solid ${C.line}` }}>{nf(c.yq)}</td>
                  <td style={{ ...td, fontWeight: 600, color: C.ink }}>{tl(c.yr)}</td>
                  <td style={td}>{pct(c.yr / top.yr, 1)}</td>
                  <td style={{ ...td, borderLeft: `1px solid ${C.line}` }}>{nf(c.lq)}</td>
                  <td style={{ ...td, paddingRight: 24 }}>{tl(c.lr)}</td>
                </tr>
              ))}
              <tr>
                <td style={{ ...tdTop, textAlign: 'left', paddingLeft: 24 }}>Total</td>
                <td style={tdTop}>{top.n}</td><td style={tdTop}>{kat.reduce((a, c) => a + c.stokta, 0)}</td><td style={tdTop} />
                <td style={{ ...tdTop, borderLeft: `1px solid ${C.line}` }}>{nf(top.yq)}</td><td style={tdTop}>{tl(top.yr)}</td><td style={tdTop}>100%</td>
                <td style={{ ...tdTop, borderLeft: `1px solid ${C.line}` }}>{nf(top.lq)}</td><td style={{ ...tdTop, paddingRight: 24 }}>{tl(top.lr)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      {kat.map((c) => (
        <div key={c.key} className="pdf-blok">
          <AltBaslik sub={`${c.n} products · ${c.stokta} in stock · ${nf(c.yq)} units / ${tl(c.yr)} in 2026 YTD`}>{c.label}</AltBaslik>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5" style={{ gap: 14 }}>
            {c.items.map((x) => (
              <Card key={x.name} style={{ padding: 12, display: 'flex', flexDirection: 'column' }}>
                <div style={{ position: 'relative' }}>
                  {x.image ? <img src={`${ASSET_DIR}/${x.image}`} alt={x.name} style={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'contain', display: 'block', borderRadius: 10, background: '#fff', opacity: x.inStock ? 1 : 0.55 }} /> : null}
                  <span style={{ position: 'absolute', top: 6, left: 6, fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 999,
                    background: x.inStock ? '#ECFDF5' : '#FEF2F2', color: x.inStock ? C.up : C.down }}>
                    {x.inStock ? `In stock${x.stockQty != null ? ` · ${x.stockQty}` : ''}` : 'Out of stock'}
                  </span>
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, marginTop: 10 }}>{x.name}</div>
                <div style={{ fontSize: 13, color: C.muted, marginTop: 2 }} className="tabular-nums">{tl(x.price)}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.hair}` }} className="tabular-nums">
                  <div><div style={{ fontSize: 11, color: C.muted }}>2026 YTD</div><div style={{ fontSize: 14, fontWeight: 700 }}>{nf(x.yq)} {x.yq === 1 ? 'unit' : 'units'}</div><div style={{ fontSize: 12, color: C.ink2 }}>{tl(x.yr)}</div></div>
                  <div><div style={{ fontSize: 11, color: C.muted }}>Last 12 mo.</div><div style={{ fontSize: 14, fontWeight: 700 }}>{nf(x.lq)} {x.lq === 1 ? 'unit' : 'units'}</div><div style={{ fontSize: 12, color: C.ink2 }}>{tl(x.lr)}</div></div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      ))}
      <Dipnot>
        Range, prices and stock from hupalupa.store on October 27, 2026; P2 Bear and P2 Fox are no longer listed on the site (last seen in the Google Merchant Center feed).
        Sales are all site sales from GA4 (incl. VAT, returns not deducted), from any channel, not ad-attributed. YTD = Jan 1 – Oct 26, 2026; last 12 months = Nov 1, 2025 – Oct 26, 2026.
      </Dipnot>
    </>
  );
}

/* ---------------------------------------------------------------- market alternatives (static) */

/**
 * Alternatives on the Turkish market, prices seen on the product pages on Oct 27, 2026
 * (public/zoo-family-assets/competitors.json). price = what the shopper pays (D&R "in-cart" price where shown), listPrice = tag price.
 * Trendyol, Hepsiburada, Amazon.com.tr and n11 blocked automated access, so VTech Kidizoom, myFirst and Kodak Smile have no TL price here.
 */
const MARKET = [
 {
  "key": "instant_kids",
  "label": "Instant-print kids cameras",
  "zoo": [
   {
    "name": "Zoo Family P2 Instant-Print Camera – Dino Green",
    "price": 6600,
    "image": "competitors/zoo-p2-dino-green.jpg",
    "note": "Thermal instant B&W print in ~3 s, 3\" color screen, ages 5+. In stock (Dino Green, Unicorn).",
    "url": "https://hupalupa.store/zoo-family-dino-green-p2-dijital-kamera-ve-fotograf-makinesi"
   }
  ],
  "items": [
   {
    "brand": "Torima",
    "name": "TRM-CMR14 Instant-Print Kids Camera (Blue)",
    "seller": "D&R",
    "price": 1799,
    "listPrice": 2499,
    "url": "https://www.dr.com.tr/elektronik/torima-cmr14-baskili-cocuk-fotograf-makinasi-mavi/foto-kamera/kamera-aksesuarlari/urunno=0002169123002",
    "image": "competitors/torima-cmr14.jpg",
    "note": "Thermal instant print, 2.4\" color screen, up to 48MP (interpolated), ages 3-12. Price is D&R in-cart price."
   },
   {
    "brand": "Bix",
    "name": "BXKP01 Instant-Print Kids Camera (Blue)",
    "seller": "D&R",
    "price": 1999,
    "listPrice": 2999,
    "url": "https://www.dr.com.tr/elektronik/bix-bxkp01-bl-1080p-30fps-48mp-sipsak-cocuk-kamerasi/foto-kamera/fotograf-makineleri/urunno=0002246347001",
    "image": "competitors/bix-bxkp01.jpg",
    "note": "Thermal instant print + Bluetooth printing, 2.4\" IPS, 1080p video, 48MP (interpolated), 1300 mAh. In-cart price; low stock."
   },
   {
    "brand": "Kodak",
    "name": "Memo Shot Era Instant Sticker Camera",
    "seller": "D&R",
    "price": 4689,
    "listPrice": null,
    "url": "https://www.dr.com.tr/elektronik/kodak-memo-shot-era-fotograf-makinesi/foto-kamera/fotograf-makineleri/urunno=0002247930001",
    "image": "competitors/kodak-memo-shot-era.jpg",
    "note": "Thermal mono sticker prints (2.7x1.8\"), also a Bluetooth phone printer; bundled with 9 label rolls. Not kids-specific."
   },
   {
    "brand": "İTHALCİM",
    "name": "Print Camera Kids Digital Camera with Thermal Printer (Pink)",
    "seller": "myavium.com (seller: Anatura)",
    "price": 999,
    "listPrice": null,
    "url": "https://myavium.com/merchant/p/print-camera-cocuk-fotograf-makinesi-dijital-hd-video-kamera-anlik-termal-resim-kagit-baskili-yazici-v-35421",
    "image": "competitors/ithalcim-print-camera.jpg",
    "note": "Generic import: thermal instant print, 10-15MP, HD video, SD card."
   }
  ]
 },
 {
  "key": "kids_digital",
  "label": "Kids digital cameras",
  "zoo": [
   {
    "name": "Zoo Family C5 Digital Camera – Dino Green",
    "price": 4950,
    "image": "competitors/zoo-c5-dino-green.jpg",
    "note": "48MP front/rear (4.8MP sensor, interpolated), 1080p video, 3\" touchscreen, ages 3+. Only Dino Green in stock.",
    "url": "https://hupalupa.store/zoo-family-dino-green-c5-dijital-kamera-ve-fotograf-makinesi"
   },
   {
    "name": "Zoo Family C1 Digital Camera – Fox",
    "price": 4390,
    "image": "competitors/zoo-c1-fox.jpg",
    "note": "Sticker-customizable, 20+ filters, removable silicone case, ~2,000 photos per charge, ages 3+. All C1 colors out of stock.",
    "url": "https://hupalupa.store/zoo-family-fox-c1-dijital-kamera-ve-fotograf-makinesi"
   }
  ],
  "items": [
   {
    "brand": "Bood",
    "name": "Dinosaur Kids Digital Camera",
    "seller": "D&R",
    "price": 999,
    "listPrice": 1499,
    "url": "https://www.dr.com.tr/elektronik/bood-dinazorlu-dijital-cocuk-kamerasi/foto-kamera/fotograf-makineleri/urunno=0002241973001",
    "image": "competitors/bood-dino.jpg",
    "note": "Dinosaur design, 2.0\" IPS, 1080p video, 40MP (interpolated), MP3 + games, microSD up to 32GB. In-cart price."
   },
   {
    "brand": "Bood",
    "name": "AF-21 Kids Camera (Blue)",
    "seller": "D&R",
    "price": 999,
    "listPrice": 1299,
    "url": "https://www.dr.com.tr/elektronik/bood-af-21-cocuk-kamerasi-mavi/foto-kamera/fotograf-makineleri/urunno=0002198459002",
    "image": "competitors/bood-af21.jpg",
    "note": "2.0\" IPS, 1080p video, 40MP (interpolated), 400 mAh, MP3 + games. In-cart price."
   },
   {
    "brand": "Bix",
    "name": "Kids X2HD Digital Camera – Bunny",
    "seller": "D&R",
    "price": 1199,
    "listPrice": 1499,
    "url": "https://www.dr.com.tr/elektronik/bix-kids-x2hd-cocuk-dijital-fotograf-makinesi-1080p-hd-kamerali-eglenceli-cocuk-kamerasi-tavsan-dese/foto-kamera/video-kameralar-/urunno=0002247194001",
    "image": "competitors/bix-x2hd-bunny.jpg",
    "note": "Bunny design, 1080p HD video, one-button photo/video. In-cart price; low stock."
   },
   {
    "brand": "Bix",
    "name": "Kids X101 Flip Camera – Unicorn (Pink)",
    "seller": "D&R",
    "price": 1399,
    "listPrice": 1799,
    "url": "https://www.dr.com.tr/elektronik/bix-kids-x101-full-hd-1080p-ekranli-donebilen-flip-baslikli-cocuk-kamerasi-pembe-unicorn-desenli/foto-kamera/video-kameralar-/urunno=0002247193002",
    "image": "competitors/bix-x101-unicorn.jpg",
    "note": "Unicorn design, 1080p, rotating flip lens for selfies, music and learning modes. In-cart price; last unit."
   },
   {
    "brand": "Piili",
    "name": "Waterproof Kids Camera",
    "seller": "D&R",
    "price": 1899,
    "listPrice": 2999,
    "url": "https://www.dr.com.tr/elektronik/piili-su-gecirmez-cocuk-kamerasi/foto-kamera/aksiyon-kameralar/urunno=0002177842001",
    "image": "competitors/piili-waterproof.jpg",
    "note": "Waterproof, 10MP, 180° rotating selfie lens, 2\" LCD, games + filters, tripod included. In-cart price."
   }
  ]
 },
 {
  "key": "instant_general",
  "label": "Instant cameras (price reference)",
  "zoo": [
   {
    "name": "Zoo Family P2 Instant-Print Camera – Dino Green",
    "price": 6600,
    "image": "competitors/zoo-p2-dino-green.jpg",
    "note": "Thermal instant B&W print in ~3 s, 3\" color screen, ages 5+. In stock (Dino Green, Unicorn).",
    "url": "https://hupalupa.store/zoo-family-dino-green-p2-dijital-kamera-ve-fotograf-makinesi"
   }
  ],
  "items": [
   {
    "brand": "Fujifilm",
    "name": "instax mini 12 (Blue)",
    "seller": "D&R",
    "price": 6399,
    "listPrice": null,
    "url": "https://www.dr.com.tr/elektronik/instax-mini-12-fotomak/foto-kamera/fotograf-makineleri/urunno=0002032563003",
    "image": "competitors/instax-mini-12.jpg",
    "note": "Analog instant film camera, color credit-card-size prints; film sold separately (20 shots ~1,159 TL at D&R)."
   },
   {
    "brand": "Polaroid",
    "name": "Go Gen 2 Everything Box (White)",
    "seller": "Novafotograf",
    "price": 6099,
    "listPrice": null,
    "url": "https://www.novafotograf.com/urun/polaroid-go-gen-2-eb-white-anlik-fotograf-makinesi",
    "image": "competitors/polaroid-go-gen2.jpg",
    "note": "Analog instant film camera sold as a bundle with Polaroid Go film; Turkish distributor warranty. Stock status unclear on page."
   },
   {
    "brand": "Kodak",
    "name": "Mini Shot 3 Era",
    "seller": "D&R",
    "price": 11189,
    "listPrice": null,
    "url": "https://www.dr.com.tr/elektronik/kodak-mini-shot-3-era-nstant-fotograf-makinesi/foto-kamera/fotograf-makineleri/urunno=0002247932001",
    "image": "competitors/kodak-mini-shot-3-era.jpg",
    "note": "Digital instant camera + Bluetooth printer, color 3x3\" 4Pass dye-sub prints. Last unit."
   }
  ]
 },
 {
  "key": "camera_cases",
  "label": "Camera cases",
  "zoo": [
   {
    "name": "Zoo Family Silicone Camera Bag – Bear",
    "price": 1250,
    "image": "competitors/zoo-silicone-bag-bear.jpg",
    "note": "Silicone carry bag for Zoo Family cameras.",
    "url": "https://hupalupa.store/zoo-family-bear-silicon-bags-31795"
   }
  ],
  "items": [
   {
    "brand": "Fujifilm",
    "name": "instax mini 12 Camera Case (Lilac)",
    "seller": "D&R",
    "price": 1099,
    "listPrice": 1599,
    "url": "https://www.dr.com.tr/elektronik/instax-mini-12-case/foto-kamera/kamera-aksesuarlari/urunno=0002054214005",
    "image": "competitors/instax-mini-12-case.jpg",
    "note": "Fits instax mini 12, 9x14.5x12.7 cm. In-cart price; low stock."
   }
  ]
 },
 {
  "key": "thermal_paper",
  "label": "Refill paper rolls",
  "zoo": [
   {
    "name": "Zoo Family 3-Pack Paper Rolls",
    "price": 790,
    "image": "competitors/zoo-paper-roll-3.jpg",
    "note": "3 thermal rolls, ~75 prints each (~225 total), BPA/BPS-free; for P-series cameras.",
    "url": "https://hupalupa.store/zoo-family-3-lu-kagit-rulo-seti"
   },
   {
    "name": "Zoo Family 3-Pack Sticker Paper Rolls",
    "price": 890,
    "image": "competitors/zoo-sticker-roll-3.jpg",
    "note": "3 self-adhesive thermal rolls for P-series cameras.",
    "url": "https://hupalupa.store/zoo-family-3-lu-yapiskan-kagit-rulo-seti"
   }
  ],
  "items": [
   {
    "brand": "Torima",
    "name": "Paper-01 3-Pack Thermal Paper Rolls (White)",
    "seller": "D&R",
    "price": 179,
    "listPrice": 399,
    "url": "https://www.dr.com.tr/elektronik/torima-paper-01-3lu-rulo-film-beyaz/foto-kamera/kamera-aksesuarlari/urunno=0002177958001",
    "image": "competitors/torima-paper-01.jpg",
    "note": "3 thermal rolls for Torima kids print cameras. In-cart price."
   },
   {
    "brand": "Torima",
    "name": "Paper-03 3-Pack Sticker Thermal Rolls (White)",
    "seller": "D&R",
    "price": 179,
    "listPrice": 399,
    "url": "https://www.dr.com.tr/elektronik/torima-paper-03-3lu-rulo-film-yapiskanli-beyaz/foto-kamera/kamera-aksesuarlari/urunno=0002177960001",
    "image": "competitors/torima-paper-03.jpg",
    "note": "3 self-adhesive thermal rolls; fits CMR14/CMR15/CMR18/C9 mini cameras. In-cart price."
   },
   {
    "brand": "Kodak",
    "name": "Memo Shot Label Rolls (9-pack)",
    "seller": "D&R",
    "price": 1289,
    "listPrice": null,
    "url": "https://www.dr.com.tr/elektronik/kodak-memo-shot-etiket-9-rulo/foto-kamera/kamera-aksesuarlari/urunno=0002247937001",
    "image": "competitors/kodak-memo-shot-labels.jpg",
    "note": "9 sticker cartridges (6 white + 3 color), up to 30 labels each, BPA-free; Kodak Memo Shot only."
   }
  ]
 },
 {
  "key": "night_lights",
  "label": "Kids night lights",
  "zoo": [
   {
    "name": "Zoo Light Kids Night Light – Dino (ZL1D)",
    "price": 2250,
    "image": "competitors/zoo-light-dino.jpg",
    "note": "3-in-1: sleep mode (30-min timer), night-walk and reading modes; 500 mAh rechargeable; soft silicone; magnetic wall mount.",
    "url": "https://hupalupa.store/zoo-light-dino-zl1d"
   }
  ],
  "items": [
   {
    "brand": "Dhink",
    "name": "Silicone Night Light – Unicorn",
    "seller": "Keyif Bebesi",
    "price": 840,
    "listPrice": null,
    "url": "https://www.keyifbebesi.com.tr/dhink-silikon-gece-lambasi-unicorn",
    "image": "competitors/dhink-unicorn.jpg",
    "note": "Soft BPA-free PVC, color-changing light, 15-min timer, 13x9x17 cm."
   },
   {
    "brand": "Dhink",
    "name": "Rechargeable Silicone Night Light – Bunny Cotton",
    "seller": "Keyif Bebesi",
    "price": 1350,
    "listPrice": null,
    "url": "https://www.keyifbebesi.com.tr/dhink-silikon-sarjli-gece-lambasi-bunny-cotton",
    "image": "competitors/dhink-bunny-rechargeable.jpg",
    "note": "Rechargeable, color-changing light, 15-min timer, BPA-free PVC."
   },
   {
    "brand": "Bylamp",
    "name": "Rechargeable Duck Night Light (Touch, Silicone)",
    "seller": "bylamp.com",
    "price": 1149.9,
    "listPrice": 1449.9,
    "url": "https://bylamp.com/products/sarjli-ordek-gece-lambasi-dokunmatik-silikon-cocuk-odasi-lamba",
    "image": "competitors/bylamp-duck.jpg",
    "note": "Touch control, soft silicone, USB-C rechargeable, 8x17 cm."
   }
  ]
 }
];

function UrunKart({ x, zoo, ref0 }) {
  const kat = ref0 && !zoo ? ref0 / x.price : null;
  return (
    <a href={x.url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
      <Card style={{ padding: 12, height: '100%', boxSizing: 'border-box', border: zoo ? `2px solid ${C.accent}` : `1px solid ${C.hair}` }}>
        <div style={{ position: 'relative' }}>
          <img src={`${ASSET_DIR}/${x.image}`} alt={x.name} style={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'contain', display: 'block', borderRadius: 10, background: '#fff' }} />
          {zoo ? <span style={{ position: 'absolute', top: 6, left: 6, fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 999, background: C.accent, color: '#fff' }}>Zoo Family</span> : null}
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 10 }}>{zoo ? 'HUPALUPA Store' : `${x.brand} · ${x.seller}`}</div>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: C.ink, marginTop: 2, lineHeight: 1.3 }}>{zoo ? x.name.replace(/^Zoo Family /, '') : x.name}</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 6, flexWrap: 'wrap' }} className="tabular-nums">
          <span style={{ fontSize: 20, fontWeight: 700, color: zoo ? C.accent : C.ink }}>{tl(x.price)}</span>
          {x.listPrice ? <span style={{ fontSize: 12.5, color: C.faint, textDecoration: 'line-through' }}>{tl(x.listPrice)}</span> : null}
        </div>
        {kat ? (
          <div style={{ fontSize: 12, fontWeight: 600, marginTop: 4, color: kat > 1.05 ? C.down : kat < 0.95 ? C.up : C.muted }}>
            Zoo Family is {kat >= 1 ? `${nf1(kat)}× this price` : `${pct(1 - kat)} cheaper`}
          </div>
        ) : null}
        {x.note ? <div style={{ fontSize: 12, color: C.muted, marginTop: 8, lineHeight: 1.45 }}>{x.note}</div> : null}
      </Card>
    </a>
  );
}

function Muadiller() {
  return (
    <>
      {MARKET.map((g) => (
        <div key={g.key} className="pdf-blok">
          <AltBaslik sub={g.zoo.length ? `Compared with ${g.zoo.map((z) => z.name.replace(/^Zoo (Family|Light) /, '')).join(', ')}` : null}>{g.label}</AltBaslik>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-6" style={{ gap: 14 }}>
            {g.zoo.map((z) => <UrunKart key={z.name} x={z} zoo />)}
            {g.items.map((x) => <UrunKart key={x.name} x={x} ref0={g.zoo[0]?.price} />)}
          </div>
        </div>
      ))}
      <Dipnot>
        Prices seen on each product page on October 27, 2026 (TRY). Struck-through price = tag price; the bold price is what the shopper pays (D&amp;R shows an "in-cart" price on many items).
        Sources: hupalupa.store, D&amp;R, Novafotograf, Keyif Bebesi, bylamp.com, myavium.com. Trendyol, Hepsiburada, Amazon.com.tr and n11 could not be read automatically,
        so VTech Kidizoom, myFirst Camera and Kodak Smile are not priced here. The multiplier compares with the first Zoo Family product in the row.
      </Dipnot>
    </>
  );
}

/* ---------------------------------------------------------------- creative assets (static) */

/**
 * Every creative used in Zoo Family ads, collected once on Oct 27, 2026 (Meta Graph API + Google Ads API, read-only).
 * Files are copied to glowyticsfrontend/public/zoo-family-assets (symlinked into glowytics-backend/public) so they keep working after the platforms' CDN links expire.
 * Spend/results are lifetime for that ad within Oct 1, 2025 – Oct 26, 2026.
 */
// Served by the backend (/pixel) — the CRA dev proxy sends *.jpg under subfolders back to itself (502).
const ASSET_DIR = `${API_BASE}/pixel/zoo-family-assets`;

const META_ADS = [
  {
    key: 'nu', title: 'Catalog ad · new customers', set: 'Glowy_Store_Sales_Catalog_MorsPlatform › NU_ZooFamily', flight: 'Feb 24 – Jul 30, 2026',
    media: [{ img: 'meta-a101f23e.jpg', label: 'Feed 1:1' }, { img: 'meta-4076cc75.jpg', label: 'Story 9:16' }],
    copyTr: 'Çocuklar dünyayı keşfederken kendi fotoğraflarını çeksin! Zoo Family çocuk fotoğraf makineleri şimdi HUPALUPA STORE’da.',
    copyEn: 'Let kids take their own photos while they explore the world! Zoo Family kids cameras, now at HUPALUPA STORE.',
    headline: 'Minik Kaşifler İçin Büyük Maceralar. (Big adventures for little explorers.)',
    spend: 54402, impressions: 331170, clicks: 6408, purchases: 45, value: 192137, link: 'https://www.instagram.com/p/DV0aVu8gILh/'
  },
  {
    key: 'col', title: 'Video + collection · parents', set: 'Glowy_Store_Sales_Video_Purchase_NU › NU_Ebeveyn_Luxury_ZooFamily', flight: 'Feb 22 – May 21, 2026',
    media: [{ video: 'meta-video-907692472147046.mp4', poster: 'meta-video-907692472147046.jpg', label: 'Video 1:1 · 22 s' }, { img: 'meta-fe748eb1.jpg', label: 'Collection cover' }],
    copyTr: 'Zoo Family ile çocuklar dünyayı kendi gözlerinden keşfediyor. 📸✨',
    copyEn: 'With Zoo Family, kids discover the world through their own eyes. 📸✨',
    headline: 'Minik Kaşifler İçin Büyük Anılar (Big memories for little explorers)',
    spend: 8624 + 14461, impressions: 63547 + 67592, clicks: 1464 + 3183, purchases: 8 + 7, value: 31740 + 33190,
    note: 'Two ads in this ad set: the video as a collection ad (₺8,624) and the same video with automatic carousel/collection format (₺14,461).',
    link: 'https://www.instagram.com/p/DVJPn8DgF2D/'
  },
  {
    key: 'pe1', title: 'Boosted Instagram post · Reels', set: 'Glowy_PostEngagement_LastPosts', flight: 'Aug 6 – Sep 9, 2026',
    media: [{ video: 'meta-video-2229306267919123.mp4', poster: 'meta-video-2229306267919123.jpg', label: 'Reels 9:16 · 23 s' }],
    copyTr: 'Zoo Family Dijital Kamera, çocukların dünyayı kendi gözlerinden keşfetmelerini sağlayan eğlenceli bir fotoğraf ve video deneyimi sunuyor. 48 MP ön ve arka kamera · Full HD video · 20’den fazla filtre ve çerçeve · yıkanabilir silikon kılıf.',
    copyEn: 'The Zoo Family digital camera gives kids a fun photo and video experience to discover the world through their own eyes. 48 MP front and back camera · Full HD video · 20+ filters and frames · washable silicone case.',
    spend: 6813, impressions: 212685, clicks: 359, videoViews: 88429, link: 'https://www.instagram.com/p/DbtGQkNAFQE/'
  },
  {
    key: 'pe2', title: 'Boosted Instagram post · Reels', set: 'Glowy_PostEngagement_LastPosts', flight: 'Sep 13 – Oct 26, 2026',
    media: [{ video: 'meta-video-1650786386615929.mp4', poster: 'meta-video-1650786386615929.jpg', label: 'Reels 9:16 · 31 s' }],
    copyTr: 'Çocukların hayal gücüne eşlik eden The Zoo Family ile her kare yeni bir keşif! 📸🦁✨ Sevimli karakterleriyle fotoğraf çekmeyi daha eğlenceli hale getir.',
    copyEn: 'With The Zoo Family by kids’ imagination’s side, every frame is a new discovery! 📸🦁✨ Cute characters make taking photos even more fun.',
    spend: 4576, impressions: 42296, clicks: 103, videoViews: 2379, link: 'https://www.instagram.com/p/DdOKMjSAB3m/'
  }
];

const YT_VIDEOS = [
  { id: '4bIVaK6ltnY', title: 'Çocukların hayal gücüne eşlik eden THE ZOOFAMILY ile her kare yeni bir keşif!', en: 'Every frame is a new discovery with THE ZOOFAMILY', used: 'Demand Gen video · Glowy_YT_ZooFamily (Oct 23, 2026 →) · ₺1,711' },
  { id: 'CBNdyW93NXc', title: 'Zoo Family Dijital Kamera, çocuklar için eğlenceli bir fotoğraf ve video deneyimi sunuyor.', en: 'A fun photo and video experience for kids', used: 'YouTube video · Glowy_YT_Products_Placement (Aug 10, 2026 →) · ₺107' },
  { id: 'VL_pm5CJiH4', title: 'Sevimli Hayvan Figürlü Kameralarla İlk Karelerini Yakala!', en: 'Capture their first shots with cute animal cameras', used: 'PMax asset group “Zoo Family Kamera” · active' },
  { id: 'MScNkNZUciU', title: 'Shortened: Sevimli Hayvan Figürlü Kameralarla İlk Karelerini Yakala!', en: 'Short cut of the video above', used: 'PMax asset group “Zoo Family Kamera” · active' },
  { id: '3G2FgYjjjd0', title: 'Her Karede Eğlence, Her Anda Keşif! 🦄', en: 'Fun in every frame, discovery in every moment', used: 'PMax asset group “Zoo Family Kamera” · active' },
  { id: 'KJhR_SihXnc', title: 'Shortened: Her Karede Eğlence, Her Anda Keşif! 🦄', en: 'Short cut of the video above', used: 'PMax asset group “Zoo Family Kamera” · active' },
  { id: 'VNkP-DkJnxY', title: 'Zoo Family kameralarla çocuklar; ilk karelerini yakalıyor ve kendi dünyalarını keşfediyor.', en: 'Kids capture their first shots and discover their own world', used: 'PMax asset group “Zoo Family Kamera” · active' },
  { id: 'Db8JwjNqo0Y', title: 'Zoo Family kameralarla çocuklar, kendi dünyalarını keşfederken en özel anlarını kaydediyor.', en: 'Kids record their most special moments', used: 'PMax asset group “Zoo Family Kamera” · removed' }
];

const PMAX_BANNERS = [
  { img: 'pmax-293940026072.jpg', label: 'Square 1200×1200' },
  { img: 'pmax-293940024137.jpg', label: 'Portrait 960×1200' },
  { img: 'pmax-294050162121.jpg', label: 'Landscape 1200×628' }
];
const PACKSHOTS = ['386085861448', '386085861469', '386085861481', '386085861553', '386085861556', '400770750241', '401314483824', '295921787034', '295921787046', '295921787055'];

function AltBaslik({ children, sub }) {
  return (
    <div style={{ margin: '26px 0 12px' }}>
      <div style={{ fontSize: 17, fontWeight: 700, color: C.ink }}>{children}</div>
      {sub ? <div style={{ fontSize: 13.5, color: C.muted, marginTop: 3 }}>{sub}</div> : null}
    </div>
  );
}

function Medya({ m }) {
  const kutu = { width: '100%', aspectRatio: '1 / 1', objectFit: 'contain', background: '#F3F4F8', borderRadius: 12, display: 'block' };
  return (
    <figure style={{ margin: 0, flex: 1, minWidth: 0 }}>
      {m.video ? (
        <video controls preload="none" playsInline poster={`${ASSET_DIR}/${m.poster}`} src={`${ASSET_DIR}/${m.video}`} style={kutu} />
      ) : (
        <a href={`${ASSET_DIR}/${m.img}`} target="_blank" rel="noreferrer"><img src={`${ASSET_DIR}/${m.img}`} alt={m.label} style={kutu} /></a>
      )}
      <figcaption style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>{m.label}</figcaption>
    </figure>
  );
}

function MetaReklam({ a }) {
  const satir = [
    ['Spend', tl(a.spend)], ['Impressions', nf(a.impressions)], ['Clicks', nf(a.clicks)],
    a.purchases != null ? ['Purchases', nf(a.purchases)] : ['Video views (3 s)', nf(a.videoViews)],
    a.value != null ? ['ROAS', roasYazi(a.value / a.spend)] : ['CTR', pct(a.clicks / a.impressions, 2)]
  ];
  return (
    <Card className="pdf-blok" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><PlatformRozet p="meta" /><span style={{ fontSize: 12.5, color: C.faint }}>· {a.flight}</span></div>
        <div style={{ fontSize: 16, fontWeight: 700, marginTop: 6 }}>{a.title}</div>
        <div style={{ fontSize: 12, color: C.faint, marginTop: 2, wordBreak: 'break-word' }}>{a.set}</div>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>{a.media.map((m) => <Medya key={m.label} m={m} />)}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 8 }} className="tabular-nums">
        {satir.map(([l, v]) => (
          <div key={l}><div style={{ fontSize: 11.5, color: C.muted }}>{l}</div><div style={{ fontSize: 14.5, fontWeight: 700, marginTop: 2 }}>{v}</div></div>
        ))}
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.5, color: C.ink2, background: '#FAFBFF', borderRadius: 10, padding: '10px 12px' }}>
        <div>{a.copyEn}</div>
        <div style={{ color: C.faint, marginTop: 4, fontSize: 12 }}>Original (TR): {a.copyTr}</div>
        {a.headline ? <div style={{ marginTop: 6, fontSize: 12.5 }}><b>Headline:</b> {a.headline}</div> : null}
      </div>
      {a.note ? <div style={{ fontSize: 12, color: C.muted }}>{a.note}</div> : null}
      <a href={a.link} target="_blank" rel="noreferrer" className="pdf-gizle" style={{ fontSize: 13, fontWeight: 600, color: C.accent }}>View on Instagram →</a>
    </Card>
  );
}

function YtKart({ v }) {
  const [oyna, setOyna] = useState(false);
  return (
    <Card className="pdf-blok" style={{ padding: 12 }}>
      {oyna ? (
        <iframe title={v.en} src={`https://www.youtube-nocookie.com/embed/${v.id}?autoplay=1`} allow="autoplay; encrypted-media" allowFullScreen
          style={{ width: '100%', aspectRatio: '16 / 9', border: 0, borderRadius: 10, display: 'block' }} />
      ) : (
        <button type="button" onClick={() => setOyna(true)} title="Play"
          style={{ position: 'relative', display: 'block', width: '100%', padding: 0, border: 0, cursor: 'pointer', borderRadius: 10, overflow: 'hidden', background: '#000' }}>
          <img src={`${ASSET_DIR}/yt-${v.id}.jpg`} alt={v.en} style={{ width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', display: 'block' }} />
          <span style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
            <span style={{ width: 48, height: 34, borderRadius: 9, background: 'rgba(220,38,38,.92)', display: 'grid', placeItems: 'center' }}>
              <span style={{ width: 0, height: 0, borderTop: '8px solid transparent', borderBottom: '8px solid transparent', borderLeft: '13px solid #fff', marginLeft: 3 }} />
            </span>
          </span>
        </button>
      )}
      <div style={{ fontSize: 13.5, fontWeight: 600, color: C.ink, marginTop: 10, lineHeight: 1.35 }}>{v.en}</div>
      <div style={{ fontSize: 12, color: C.faint, marginTop: 3, lineHeight: 1.4 }}>{v.title}</div>
      <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>{v.used}</div>
      <a href={`https://www.youtube.com/watch?v=${v.id}`} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: C.accent, fontWeight: 600 }}>youtube.com/watch?v={v.id}</a>
    </Card>
  );
}

function Assetler() {
  return (
    <>
      <AltBaslik sub="Each card is one Meta ad set: the creatives, the copy (translated) and what it delivered.">Meta ads (Facebook + Instagram)</AltBaslik>
      <div className="grid grid-cols-1 xl:grid-cols-2" style={{ gap: 14 }}>
        {META_ADS.map((a) => <MetaReklam key={a.key} a={a} />)}
      </div>

      <AltBaslik sub="Used in Performance Max, Demand Gen and YouTube campaigns. Click a thumbnail to play.">Google · YouTube videos</AltBaslik>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4" style={{ gap: 14 }}>
        {YT_VIDEOS.map((v) => <YtKart key={v.id} v={v} />)}
      </div>

      <AltBaslik sub="Designed banners in the “Zoo Family Kamera” Performance Max asset group (active since Sep 29, 2025). The PMax asset group spent ₺159K between Oct 2025 and Oct 2026 across Shopping, Display, YouTube and Search; Google does not report spend per image.">Google · Performance Max banners</AltBaslik>
      <Card style={{ padding: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 0.8fr 1.9fr', gap: 12, alignItems: 'end' }}>
          {PMAX_BANNERS.map((b) => (
            <figure key={b.img} style={{ margin: 0 }}>
              <img src={`${ASSET_DIR}/${b.img}`} alt={b.label} style={{ width: '100%', display: 'block', borderRadius: 10 }} />
              <figcaption style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>{b.label}</figcaption>
            </figure>
          ))}
        </div>
      </Card>

      <AltBaslik sub="Product images that rotated in the same PMax asset group earlier (now removed in favour of the banners above).">Google · Product packshots</AltBaslik>
      <Card style={{ padding: 16 }}>
        <div className="grid grid-cols-3 sm:grid-cols-5 xl:grid-cols-10" style={{ gap: 10 }}>
          {PACKSHOTS.map((id) => (
            <img key={id} src={`${ASSET_DIR}/pack-${id}.jpg`} alt="Zoo Family product"
              style={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'contain', background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 10 }} />
          ))}
        </div>
        <Dipnot>
          Search ads (Glowy_Search_ZooFamily, ₺38.5K) and dynamic search ads are text-only and not shown here. TikTok ran a single traffic ad group (Sep 1–16, ₺1,661);
          its creative is not included. Catalog ads also rotate product photos from the store feed automatically.
        </Dipnot>
      </Card>
    </>
  );
}

/* ---------------------------------------------------------------- search demand (static) */

/**
 * Google Keyword Planner snapshot, pulled once on Oct 27, 2026 (HUPALUPA Store Google Ads account,
 * Turkey, Turkish, Google Search). Hard-coded on purpose so the numbers do not change on every visit.
 * m = monthly searches Oct 2025 → Sep 2026; cpc = top-of-page bid range in TRY (low, high).
 */
const KW_MONTHS = ['2025-10', '2025-11', '2025-12', '2026-01', '2026-02', '2026-03', '2026-04', '2026-05', '2026-06', '2026-07', '2026-08', '2026-09'];
const KW_GROUPS = [
  { key: 'brand', label: 'Zoo Family (brand)', rows: [
    { kw: "zoo family", en: "", avg: 70, m: [50, 40, 110, 70, 50, 70, 70, 90, 110, 90, 70, 90], cpc: [7.86, 135.04] },
    { kw: "zoo family fotoğraf makinesi", en: "zoo family photo camera", avg: 50, m: [0, 0, 140, 90, 50, 70, 50, 50, 70, 20, 40, 50], cpc: [6.31, 101.55] },
    { kw: "zoo family kamera", en: "zoo family camera", avg: 10, m: [10, 10, 10, 20, 10, 10, 10, 10, 10, 10, 10, 10], cpc: [3.91, 68.32] },
    { kw: "zoo light", en: "", avg: 10, m: [0, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 0], cpc: [null, null] },
  ] },
  { key: 'category', label: 'Kids camera (category)', rows: [
    { kw: "çocuk fotoğraf makinesi", en: "kids photo camera", avg: 14800, m: [9900, 12100, 14800, 18100, 14800, 22200, 18100, 14800, 18100, 12100, 12100, 9900], cpc: [2.09, 15.28] },
    { kw: "çocuk kamerası", en: "kids camera", avg: 3600, m: [2900, 2900, 3600, 4400, 3600, 5400, 3600, 3600, 4400, 3600, 3600, 2900], cpc: [1.74, 12.6] },
    { kw: "çocuklar için fotoğraf makinesi", en: "camera for children", avg: 2900, m: [2400, 2400, 2900, 3600, 3600, 4400, 3600, 2900, 3600, 2400, 2400, 2400], cpc: [2.64, 13.14] },
    { kw: "çocuk fotoğraf makinesi baskılı", en: "kids camera with printing", avg: 1300, m: [320, 480, 590, 1000, 1300, 1900, 1600, 1600, 1600, 1300, 1300, 1600], cpc: [2.79, 15.77] },
    { kw: "kids camera", en: "", avg: 480, m: [260, 320, 390, 1000, 590, 880, 480, 480, 480, 260, 260, 320], cpc: [2.3, 12.11] },
    { kw: "dijital fotoğraf makinesi çocuk", en: "digital camera kids", avg: 260, m: [70, 110, 170, 320, 320, 320, 320, 260, 320, 320, 260, 260], cpc: [2.15, 13.97] },
    { kw: "çocuk dijital kamera", en: "kids digital camera", avg: 70, m: [30, 50, 70, 70, 90, 90, 70, 140, 90, 70, 50, 40], cpc: [1.67, 17.13] },
    { kw: "çocuk fotoğraf makinesi anında baskı", en: "kids camera instant print", avg: 40, m: [40, 40, 50, 40, 30, 50, 50, 40, 50, 40, 40, 40], cpc: [3.83, 15.66] },
  ] },
  { key: 'instant', label: 'Instant-print cameras (competitors)', rows: [
    { kw: "instax mini 12", en: "", avg: 27100, m: [22200, 22200, 27100, 33100, 40500, 27100, 27100, 27100, 27100, 27100, 27100, 22200], cpc: [2.05, 8.1] },
    { kw: "instax mini", en: "", avg: 27100, m: [22200, 22200, 27100, 33100, 40500, 40500, 33100, 27100, 33100, 27100, 27100, 22200], cpc: [1.27, 5.69] },
    { kw: "polaroid", en: "", avg: 14800, m: [14800, 14800, 12100, 12100, 12100, 9900, 9900, 12100, 12100, 12100, 27100, 12100], cpc: [3.16, 12.5] },
    { kw: "mini fotoğraf makinesi", en: "mini photo camera", avg: 6600, m: [2400, 2900, 4400, 5400, 8100, 6600, 6600, 6600, 9900, 8100, 8100, 6600], cpc: [1.37, 9.11] },
    { kw: "fujifilm instax", en: "", avg: 3600, m: [2400, 2400, 3600, 4400, 4400, 3600, 2900, 4400, 3600, 3600, 3600, 2900], cpc: [2.4, 9.71] },
    { kw: "instax mini 41", en: "", avg: 1300, m: [590, 880, 1300, 1300, 2900, 1600, 1300, 1300, 1000, 1000, 1600, 1000], cpc: [3.57, 10.88] },
    { kw: "anlık fotoğraf makinesi", en: "instant camera", avg: 1000, m: [1000, 1000, 1300, 1300, 1300, 1300, 1000, 1000, 880, 880, 880, 880], cpc: [1.57, 5.88] },
    { kw: "anında baskı fotoğraf makinesi", en: "instant print camera", avg: 480, m: [480, 480, 480, 590, 590, 320, 320, 480, 590, 590, 590, 590], cpc: [2.89, 8.48] },
    { kw: "polaroid go", en: "", avg: 390, m: [320, 320, 480, 390, 480, 390, 260, 320, 390, 320, 390, 260], cpc: [3.86, 12.14] },
    { kw: "kodak printomatic", en: "", avg: 210, m: [110, 110, 170, 210, 170, 260, 210, 260, 260, 320, 390, 260], cpc: [2.99, 10.25] },
    { kw: "baskı yapan fotoğraf makinesi", en: "camera that prints", avg: 140, m: [90, 110, 140, 210, 210, 170, 140, 140, 140, 140, 110, 110], cpc: [1.8, 6.91] },
    { kw: "kodak smile", en: "", avg: 30, m: [20, 20, 20, 40, 40, 40, 40, 50, 50, 40, 20, 20], cpc: [null, null] },
  ] },
  { key: 'kids', label: 'Kids camera brands (competitors)', rows: [
    { kw: "kidizoom", en: "", avg: 1600, m: [6600, 1900, 1600, 1300, 720, 1300, 1900, 1000, 1300, 880, 880, 720], cpc: [4.55, 21.42] },
    { kw: "vtech kidizoom", en: "", avg: 140, m: [110, 90, 140, 140, 90, 140, 140, 140, 140, 140, 170, 110], cpc: [4.75, 13.07] },
    { kw: "myfirst camera", en: "", avg: 10, m: [10, 10, 10, 10, 0, 10, 10, 10, 20, 20, 20, 10], cpc: [5.67, 60.65] },
  ] },
  { key: 'lamp', label: 'Night lights (Zoo Light)', rows: [
    { kw: "çocuk gece lambası", en: "kids night light", avg: 1900, m: [1900, 2400, 2400, 2400, 2400, 2400, 1900, 1600, 1300, 1600, 1600, 2400], cpc: [1.09, 11.3] },
    { kw: "bebek gece lambası", en: "baby night light", avg: 590, m: [880, 720, 720, 720, 720, 590, 590, 480, 390, 480, 480, 590], cpc: [2.35, 13.98] },
    { kw: "unicorn gece lambası", en: "unicorn night light", avg: 140, m: [140, 140, 140, 140, 140, 170, 140, 90, 90, 110, 110, 140], cpc: [2.49, 12.19] },
    { kw: "dinozor gece lambası", en: "dinosaur night light", avg: 70, m: [40, 90, 70, 50, 70, 70, 40, 40, 50, 70, 40, 260], cpc: [1.86, 10.59] },
  ] },
  { key: 'paper', label: 'Refill paper', rows: [
    { kw: "fotoğraf makinesi kağıdı", en: "camera paper", avg: 390, m: [210, 320, 320, 390, 480, 480, 390, 480, 390, 320, 390, 320], cpc: [1.48, 6.45] },
    { kw: "termal kağıt rulo", en: "thermal paper roll", avg: 90, m: [50, 70, 70, 90, 110, 140, 90, 70, 70, 90, 140, 140], cpc: [3.75, 17.88] },
  ] },
];

function Spark({ m }) {
  const max = Math.max(1, ...m);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 26, width: 132 }}>
      {m.map((v, i) => (
        <div key={KW_MONTHS[i]} title={`${ayTam(KW_MONTHS[i])}: ${nf(v)}`}
          style={{ flex: 1, height: `${Math.max(v ? 8 : 0, (v / max) * 100)}%`, background: v === max ? C.accent : '#C7D3FA', borderRadius: 2 }} />
      ))}
    </div>
  );
}

function AramaHacmi() {
  const grupToplam = (g) => g.rows.reduce((a, r) => a + r.avg, 0);
  const tumMax = Math.max(...KW_GROUPS.flatMap((g) => g.rows.map((r) => r.avg)));
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6 pdf-kpi" style={{ gap: 14 }}>
        {KW_GROUPS.map((g) => (
          <Card key={g.key} style={{ padding: '16px 18px' }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: C.muted }}>{g.label}</div>
            <div style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em', marginTop: 6 }} className="tabular-nums">{sayiKisa(grupToplam(g))}</div>
            <div style={{ fontSize: 12, color: C.faint, marginTop: 2 }}>searches / month · {g.rows.length} keywords</div>
          </Card>
        ))}
      </div>
      <Card style={{ padding: '8px 0 4px', marginTop: 14 }}>
        <div className="overflow-x-auto">
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13.5, minWidth: 1100 }} className="tabular-nums">
            <thead>
              <tr>
                <th style={{ ...th, textAlign: 'left', paddingLeft: 24 }}>Keyword (Turkish)</th>
                <th style={{ ...th, textAlign: 'left' }}>Meaning</th>
                <th style={{ ...th, textAlign: 'left' }}>Avg. monthly searches</th>
                <th style={{ ...th, textAlign: 'left' }}>Oct 2025 – Sep 2026</th>
                <th style={th}>Peak month</th>
                <th style={{ ...th, paddingRight: 24 }}>CPC range (TRY)</th>
              </tr>
            </thead>
            <tbody>
              {KW_GROUPS.map((g) => (
                <React.Fragment key={g.key}>
                  <tr>
                    <td colSpan={6} style={{ ...td, textAlign: 'left', paddingLeft: 24, background: '#FAFBFF', fontWeight: 700, color: C.ink, fontSize: 12.5, borderTop: `2px solid ${C.line}` }}>
                      {g.label} <span style={{ color: C.faint, fontWeight: 500 }}>· {nf(grupToplam(g))} / month</span>
                    </td>
                  </tr>
                  {g.rows.map((r) => {
                    const pi = r.m.indexOf(Math.max(...r.m));
                    return (
                      <tr key={r.kw}>
                        <td style={{ ...td, textAlign: 'left', paddingLeft: 24, fontWeight: 600, color: C.ink }}>{r.kw}</td>
                        <td style={{ ...td, textAlign: 'left', color: C.muted }}>{r.en || '—'}</td>
                        <td style={{ ...td, textAlign: 'left' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ minWidth: 52, fontWeight: 600, color: C.ink, textAlign: 'right' }}>{nf(r.avg)}</span>
                            <div style={{ width: 120, height: 6, background: C.hair, borderRadius: 3 }}>
                              <div style={{ width: `${Math.max(1, (r.avg / tumMax) * 100)}%`, height: 6, borderRadius: 3, background: g.key === 'brand' ? '#eb6834' : C.accent }} />
                            </div>
                          </div>
                        </td>
                        <td style={{ ...td, textAlign: 'left' }}><Spark m={r.m} /></td>
                        <td style={td}>{Math.max(...r.m) ? `${AY[Number(KW_MONTHS[pi].slice(5, 7)) - 1]} ${KW_MONTHS[pi].slice(0, 4)}` : '—'}</td>
                        <td style={{ ...td, paddingRight: 24 }}>{r.cpc[0] != null ? `${tl2(r.cpc[0])} – ${tl2(r.cpc[1])}` : '—'}</td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <Dipnot>
          Source: Google Keyword Planner (Turkey, Turkish, Google Search), snapshot taken October 27, 2026; figures are fixed and do not refresh.
          Volumes are Google's rounded estimates. Keywords with too little volume to report (e.g. "çocuk polaroid", "çocuk instax") are left out.
          CPC range = Google's low and high top-of-page bid estimates.
        </Dipnot>
      </Card>
    </>
  );
}

/* ---------------------------------------------------------------- brand search in Europe (static) */

/**
 * Google Keyword Planner by country, all languages, Google Search; snapshot Oct 27, 2026 (avg. monthly searches Oct 2025 – Sep 2026).
 * Row: [code, country, the zoo family, zoo family, zoo family camera, the zoo family camera, hoppstar, kidizoom, monthly 'the zoo family' x12].
 * Google merges close variants, so 'zoofamily' = 'zoo family'. 10 = Google's floor ("0–10").
 */
const EU_KW = [
  ["FR", "France", 880, 1300, 30, 20, 90, 18100, [1300, 2900, 2400, 590, 480, 480, 480, 590, 480, 590, 590, 720]],
  ["BE", "Belgium", 210, 210, 140, 110, 40, 1600, [260, 590, 320, 140, 90, 170, 140, 140, 110, 140, 140, 170]],
  ["UK", "United Kingdom", 140, 170, 210, 70, 90, 1000, [170, 260, 390, 90, 110, 90, 90, 70, 70, 70, 70, 90]],
  ["DE", "Germany", 140, 140, 20, 10, 390, 2900, [110, 210, 260, 140, 90, 170, 90, 110, 90, 170, 110, 140]],
  ["ES", "Spain", 140, 110, 40, 30, 140, 720, [110, 210, 210, 210, 140, 90, 90, 70, 70, 110, 90, 140]],
  ["PL", "Poland", 110, 140, 10, 10, 140, 260, [110, 260, 260, 90, 70, 90, 110, 140, 70, 70, 70, 70]],
  ["CH", "Switzerland", 110, 140, 20, 20, 70, 1000, [70, 210, 260, 140, 90, 140, 90, 110, 70, 90, 90, 110]],
  ["IT", "Italy", 90, 590, 10, 20, 210, 260, [90, 260, 210, 50, 50, 90, 70, 70, 90, 90, 50, 70]],
  ["NL", "Netherlands", 70, 70, 90, 70, 20, 1000, [90, 210, 90, 70, 20, 70, 40, 30, 40, 50, 50, 50]],
  ["TR", "Turkey", 50, 70, 70, 40, 90, 1600, [20, 30, 70, 70, 50, 70, 40, 40, 40, 30, 70, 50]],
  ["CZ", "Czechia", 40, 170, 10, 10, 10, 390, [40, 110, 90, 30, 20, 30, 40, 30, 10, 30, 30, 30]],
  ["DK", "Denmark", 40, 30, 10, 10, 10, 20, [30, 90, 110, 20, 10, 20, 20, 70, 40, 50, 20, 20]],
  ["SK", "Slovakia", 30, 50, 10, 10, 20, 170, [40, 90, 70, 30, 10, 40, 10, 10, 20, 10, 20, 10]],
  ["PT", "Portugal", 30, 30, 10, 10, 40, 260, [40, 50, 50, 20, 10, 30, 10, 40, 20, 30, 20, 10]],
  ["GR", "Greece", 20, 170, 10, 10, 10, 30, [20, 20, 30, 10, 20, 20, 30, 10, 20, 10, 10, 20]],
  ["HU", "Hungary", 20, 40, 10, 10, 10, 70, [10, 50, 50, 10, 20, 10, 20, 20, 20, 30, 10, 20]],
  ["BG", "Bulgaria", 20, 20, 10, 10, 10, 10, [10, 30, 20, 20, 20, 10, 10, 20, 20, 10, 20, 10]],
  ["SE", "Sweden", 10, 20, 10, 10, 10, 20, [10, 20, 30, 10, 10, 10, 10, 10, 10, 10, 10, 10]],
  ["AT", "Austria", 10, 20, 10, 10, 140, 320, [10, 20, 20, 10, 20, 10, 10, 10, 10, 10, 10, 10]],
  ["HR", "Croatia", 10, 20, 10, 10, 20, 10, [10, 20, 30, 10, 10, 10, 10, 10, 10, 10, 10, 10]],
  ["SI", "Slovenia", 10, 20, 10, 10, 30, 10, [10, 10, 10, 10, 10, 0, 10, 10, 10, 10, 0, 10]],
  ["LT", "Lithuania", 10, 20, 10, 10, 10, 10, [20, 20, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10]],
  ["EE", "Estonia", 10, 20, 10, 10, 10, 10, [10, 10, 10, 10, 10, 10, 0, 10, 0, 10, 10, 10]],
  ["LU", "Luxembourg", 10, 20, 10, 10, 10, 30, [10, 30, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10]],
  ["NO", "Norway", 10, 10, 10, 10, 10, 10, [10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 0]],
  ["FI", "Finland", 10, 10, 10, 10, 30, 10, [10, 10, 10, 0, 0, 10, 10, 10, 10, 10, 10, 10]],
  ["IE", "Ireland", 10, 10, 10, 10, 10, 110, [10, 10, 20, 10, 10, 10, 10, 10, 10, 10, 10, 10]],
  ["RO", "Romania", 10, 10, 10, 10, 20, 40, [10, 10, 10, 10, 10, 20, 20, 20, 10, 20, 10, 10]],
  ["LV", "Latvia", 10, 10, 10, 10, 10, 10, [0, 10, 10, 10, 0, 10, 0, 10, 0, 0, 0, 10]],
  ["CY", "Cyprus", 10, 10, 10, 10, 10, 10, [10, 10, 10, 10, 0, 10, 0, 10, 10, 10, 0, 0]],
];

function AvrupaArama() {
  const eu = EU_KW.filter((r) => r[0] !== 'TR');
  const sum = (i) => eu.reduce((a, r) => a + r[i], 0);
  const max = Math.max(...EU_KW.map((r) => r[2]));
  const kol = [['the zoo family', 2], ['zoo family', 3], ['zoo family camera', 4], ['the zoo family camera', 5]];
  const ref = [['hoppstar', 6], ['kidizoom', 7]];
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 pdf-kpi" style={{ gap: 14 }}>
        {[...kol].map(([l, i]) => (
          <Card key={l} style={{ padding: '16px 18px' }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: C.muted }}>“{l}” · Europe</div>
            <div style={{ fontSize: 26, fontWeight: 700, marginTop: 6 }} className="tabular-nums">{nf(sum(i))}</div>
            <div style={{ fontSize: 12, color: C.faint, marginTop: 2 }}>searches / month · {eu.length} countries</div>
          </Card>
        ))}
      </div>
      <Card style={{ padding: '8px 0 4px', marginTop: 14 }}>
        <div className="overflow-x-auto">
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13.5, minWidth: 1100 }} className="tabular-nums">
            <thead>
              <tr>
                <th style={{ ...th, textAlign: 'left', paddingLeft: 24 }}>Country</th>
                {kol.map(([l]) => <th key={l} style={th}>“{l}”</th>)}
                <th style={{ ...th, textAlign: 'left', paddingLeft: 18 }}>“the zoo family” Oct 2025 – Sep 2026</th>
                {ref.map(([l], j) => <th key={l} style={{ ...th, borderLeft: j === 0 ? `1px solid ${C.line}` : undefined, paddingRight: j === ref.length - 1 ? 24 : 10 }}>“{l}”</th>)}
              </tr>
            </thead>
            <tbody>
              {EU_KW.map((r) => (
                <tr key={r[0]} style={{ background: r[0] === 'TR' ? '#FAFBFF' : undefined }}>
                  <td style={{ ...td, textAlign: 'left', paddingLeft: 24, fontWeight: 600, color: C.ink }}>{r[1]}{r[0] === 'TR' ? <span style={{ color: C.faint, fontWeight: 500 }}> · reference</span> : null}</td>
                  {kol.map(([l, i]) => (
                    <td key={l} style={{ ...td, fontWeight: i === 2 ? 700 : 400, color: r[i] <= 10 ? C.faint : i === 2 ? C.ink : C.ink2 }}>
                      {i === 2 ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ width: 70, height: 6, background: C.hair, borderRadius: 3, display: 'inline-block' }}>
                            <span style={{ display: 'block', width: `${(r[i] / max) * 100}%`, height: 6, borderRadius: 3, background: '#eb6834' }} />
                          </span>
                          {r[i] <= 10 ? '≤10' : nf(r[i])}
                        </span>
                      ) : (r[i] <= 10 ? '≤10' : nf(r[i]))}
                    </td>
                  ))}
                  <td style={{ ...td, textAlign: 'left', paddingLeft: 18 }}>{r[2] > 10 ? <Spark m={r[8]} /> : <span style={{ color: C.faint }}>too low to chart</span>}</td>
                  {ref.map(([l, i], j) => (
                    <td key={l} style={{ ...td, color: r[i] <= 10 ? C.faint : C.ink2, borderLeft: j === 0 ? `1px solid ${C.line}` : undefined, paddingRight: j === ref.length - 1 ? 24 : 10 }}>{r[i] <= 10 ? '≤10' : nf(r[i])}</td>
                  ))}
                </tr>
              ))}
              <tr>
                <td style={{ ...tdTop, textAlign: 'left', paddingLeft: 24 }}>Europe total <span style={{ color: C.faint, fontWeight: 500, fontSize: 12.5 }}>· excl. Turkey</span></td>
                {kol.map(([l, i]) => <td key={l} style={tdTop}>{nf(sum(i))}</td>)}
                <td style={tdTop} />
                {ref.map(([l, i], j) => <td key={l} style={{ ...tdTop, borderLeft: j === 0 ? `1px solid ${C.line}` : undefined, paddingRight: j === ref.length - 1 ? 24 : 10 }}>{nf(sum(i))}</td>)}
              </tr>
            </tbody>
          </table>
        </div>
        <Dipnot>
          Source: Google Keyword Planner, each country separately, all languages, Google Search; snapshot taken October 27, 2026 (average of Oct 2025 – Sep 2026). Figures are fixed and do not refresh.
          Google merges close variants, so “zoofamily” counts as “zoo family”. “zoo family” on its own is a generic phrase (zoo family tickets, family zoo visits), so in countries such as France,
          Italy, Greece and Czechia part of that volume is likely not the brand; “the zoo family” and “… camera” are the cleaner brand signals. ≤10 = below Google’s reporting floor; totals count these as 10.
          Hoppstar and Kidizoom are shown for scale.
        </Dipnot>
      </Card>
    </>
  );
}

/**
 * Generic "kids camera" searches by country in the local language(s), Keyword Planner, snapshot Oct 27, 2026 (avg. Oct 2025 – Sep 2026).
 * Close variants merged, each Google result counted once. Google returned no volume for several child-related terms (all of France, Italy,
 * Luxembourg; some long-tail terms elsewhere), so totals are a floor. Row: [code, country, total, top terms [[kw, avg]], monthly total x12].
 */
const EU_GENERIC = [
  ["PL", "Poland", 28400, [["aparat dla dzieci", 18100], ["aparat fotograficzny dla dzieci", 5400], ["aparat natychmiastowy dla dzieci", 3600]], [20100, 55400, 69600, 19280, 18980, 23100, 19780, 30400, 16620, 18980, 18820, 19320]],
  ["TR", "Turkey", 23450, [["çocuk fotoğraf makinesi", 14800], ["çocuk kamerası", 3600], ["çocuklar için fotoğraf makinesi", 2900]], [15920, 18400, 22570, 28530, 24330, 35240, 27820, 23820, 28640, 20090, 20010, 17460]],
  ["UK", "United Kingdom", 18100, [["kids camera", 18100]], [22200, 40500, 33100, 9900, 8100, 9900, 9900, 9900, 9900, 12100, 14800, 18100]],
  ["DE", "Germany", 12100, [["kinderkamera", 12100]], [14800, 27100, 22200, 8100, 6600, 12100, 8100, 8100, 8100, 9900, 9900, 9900]],
  ["RO", "Romania", 4710, [["aparat foto copii", 3600], ["camera foto copii", 720], ["aparat foto pentru copii", 390]], [3880, 9990, 12520, 4510, 3200, 3810, 3700, 3310, 3200, 3880, 3880, 3380]],
  ["SE", "Sweden", 4240, [["kamera barn", 1600], ["barnkamera", 880], ["kamera för barn", 880]], [3330, 8400, 11100, 3500, 2350, 2660, 2660, 3460, 3460, 4080, 4080, 3630]],
  ["HU", "Hungary", 3830, [["gyerek fényképezőgép", 2900], ["fényképezőgép gyerekeknek", 720], ["gyerek kamera", 210]], [3790, 8680, 10480, 2520, 2130, 3130, 2060, 3160, 3050, 3750, 3700, 3700]],
  ["NL", "Netherlands", 3380, [["kindercamera", 2900], ["camera voor kinderen", 480]], [4990, 7480, 4190, 2880, 2790, 2790, 2790, 3380, 3380, 4190, 3380, 2880]],
  ["DK", "Denmark", 3190, [["børnekamera", 1300], ["kamera til børn", 1300], ["børne kamera", 590]], [2990, 7380, 6180, 2470, 1860, 1950, 1950, 2080, 2240, 2720, 2470, 2080]],
  ["CZ", "Czechia", 2800, [["dětský fotoaparát", 1600], ["fotoaparát pro děti", 720], ["detsky fotoaparat", 480]], [3870, 8600, 5900, 1690, 1320, 1370, 1150, 1530, 1580, 2100, 2100, 2170]],
  ["GR", "Greece", 2780, [["παιδικη φωτογραφικη μηχανη", 1600], ["παιδική φωτογραφική μηχανή", 590], ["φωτογραφική μηχανή για παιδιά", 590]], [1640, 4400, 10900, 2670, 1300, 2560, 2170, 1530, 1520, 1780, 1710, 1460]],
  ["PT", "Portugal", 2470, [["máquina fotográfica infantil", 1300], ["máquina fotográfica criança", 1000], ["câmera infantil", 90]], [2010, 5570, 6810, 1890, 1390, 1740, 1720, 1730, 1710, 1900, 1930, 1950]],
  ["NO", "Norway", 2370, [["kamera barn", 1300], ["kamera til barn", 590], ["barnekamera", 480]], [2500, 5500, 5080, 1530, 1010, 1250, 1060, 1660, 1750, 2480, 1750, 1960]],
  ["FI", "Finland", 2020, [["lasten kamera", 1600], ["lasten digikamera", 390], ["kamera lapsille", 30]], [1650, 4550, 5470, 1280, 870, 950, 1150, 1100, 1350, 1720, 1710, 1410]],
  ["CH", "Switzerland", 1600, [["kinderkamera", 1600]], [1900, 4400, 3600, 1000, 880, 1300, 1000, 1000, 880, 1000, 1300, 1300]],
  ["ES", "Spain", 1300, [["cámara de fotos infantil", 1300]], [880, 2400, 3600, 1300, 720, 880, 880, 1000, 880, 880, 1000, 1300]],
  ["AT", "Austria", 1000, [["kinderkamera", 1000]], [1300, 2400, 2400, 880, 720, 1300, 720, 720, 720, 880, 880, 880]],
  ["BE", "Belgium", 980, [["kindercamera", 720], ["camera voor kinderen", 260]], [1200, 2190, 1390, 690, 560, 800, 620, 690, 530, 690, 650, 560]],
  ["IE", "Ireland", 880, [["kids camera", 880]], [1000, 2900, 1900, 590, 480, 590, 590, 590, 590, 590, 720, 880]],
  ["SK", "Slovakia", 870, [["fotoaparát pre deti", 480], ["detský fotoaparát", 390]], [1310, 2600, 2300, 650, 490, 350, 420, 470, 420, 530, 520, 530]],
  ["FR", "France", 0, [], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]],
  ["LU", "Luxembourg", 0, [], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]],
  ["IT", "Italy", 0, [], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]],
];

function AvrupaGenel() {
  const eu = EU_GENERIC.filter((r) => r[0] !== 'TR');
  const tot = eu.reduce((a, r) => a + r[2], 0);
  const max = Math.max(...EU_GENERIC.map((r) => r[2]));
  return (
    <Card style={{ padding: '8px 0 4px', marginTop: 14 }}>
      <div style={{ padding: '14px 24px 6px', display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>Generic “kids camera” searches, local language</div>
        <div style={{ fontSize: 13, color: C.muted }} className="tabular-nums">{nf(tot)} / month across {eu.filter((r) => r[2] > 0).length} European countries with data</div>
      </div>
      <div className="overflow-x-auto">
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13.5, minWidth: 1000 }} className="tabular-nums">
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left', paddingLeft: 24 }}>Country</th>
              <th style={{ ...th, textAlign: 'left' }}>Searches / month</th>
              <th style={{ ...th, textAlign: 'left' }}>Oct 2025 – Sep 2026</th>
              <th style={{ ...th, textAlign: 'left', paddingRight: 24 }}>Main search terms</th>
            </tr>
          </thead>
          <tbody>
            {EU_GENERIC.map((r) => (
              <tr key={r[0]} style={{ background: r[0] === 'TR' ? '#FAFBFF' : undefined }}>
                <td style={{ ...td, textAlign: 'left', paddingLeft: 24, fontWeight: 600, color: C.ink }}>{r[1]}{r[0] === 'TR' ? <span style={{ color: C.faint, fontWeight: 500 }}> · reference</span> : null}</td>
                <td style={{ ...td, textAlign: 'left' }}>
                  {r[2] ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ minWidth: 56, fontWeight: 700, color: C.ink, textAlign: 'right' }}>{nf(r[2])}</span>
                      <div style={{ width: 140, height: 6, background: C.hair, borderRadius: 3 }}><div style={{ width: `${(r[2] / max) * 100}%`, height: 6, borderRadius: 3, background: C.accent }} /></div>
                    </div>
                  ) : <span style={{ color: C.faint }}>not reported by Google</span>}
                </td>
                <td style={{ ...td, textAlign: 'left' }}>{r[2] ? <Spark m={r[4]} /> : null}</td>
                <td style={{ ...td, textAlign: 'left', paddingRight: 24, whiteSpace: 'normal', color: C.muted, fontSize: 12.5 }}>
                  {r[3].map(([k, v]) => `${k} (${nf(v)})`).join(' · ') || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dipnot>
        Each country is queried in its own language(s) with 3–8 common phrasings (e.g. “kids camera”, “Kinderkamera”, “aparat dla dzieci”); close variants merged and each result counted once.
        Google Keyword Planner withholds volume for many child-related search terms: France, Italy and Luxembourg return no data at all, and some phrasings in other countries are missing,
        so these totals are a floor. As a proxy for France, “kidizoom” alone is searched ~18,100 times a month there (table above). Snapshot taken October 27, 2026; figures do not refresh.
      </Dipnot>
    </Card>
  );
}

/* ---------------------------------------------------------------- tarih seçici */

function TarihSecici({ araKey, secAralik, ozel, setOzel, uygulaOzel, range, loading, onYenile }) {
  const [acik, setAcik] = useState(false);
  const kutu = useRef(null);
  const dun = aralikOf('30')[1];

  useEffect(() => {
    if (!acik) return undefined;
    const disari = (e) => { if (kutu.current && !kutu.current.contains(e.target)) setAcik(false); };
    const esc = (e) => { if (e.key === 'Escape') setAcik(false); };
    document.addEventListener('mousedown', disari);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', disari); document.removeEventListener('keydown', esc); };
  }, [acik]);

  const etiket = ARALIKLAR.find((a) => a.key === araKey)?.label || 'Date range';
  const kisaAralik = (r) => (r ? aralikEtiket(r[0], r[1]) : '');
  const tarihInput = { width: '100%', minWidth: 0, boxSizing: 'border-box', border: `1px solid ${C.line}`, borderRadius: 8, padding: '6px 6px', background: '#fff', fontSize: 13, color: C.ink, fontFamily: FONT };

  return (
    <div ref={kutu} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={() => setAcik((v) => !v)} aria-expanded={acik}
          style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', cursor: 'pointer', fontFamily: FONT,
            background: '#fff', border: `1px solid ${acik ? C.accent : C.line}`, borderRadius: 12, padding: '9px 12px',
            boxShadow: acik ? `0 0 0 3px ${C.accentSoft}` : '0 1px 2px rgba(17,24,39,.05)', transition: 'border-color .15s, box-shadow .15s' }}>
          <span style={{ width: 30, height: 30, borderRadius: 8, background: C.accentSoft, color: C.accent, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <Calendar size={15} />
          </span>
          <span style={{ minWidth: 0, flex: 1 }}>
            <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: C.ink, lineHeight: 1.2 }}>{etiket}</span>
            <span style={{ display: 'block', fontSize: 12, color: C.muted, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} className="tabular-nums">
              {kisaAralik(range)}
            </span>
          </span>
          <ChevronDown size={16} style={{ color: C.faint, flexShrink: 0, transform: acik ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
        </button>
        <button type="button" onClick={onYenile} title="Refresh data"
          style={{ border: `1px solid ${C.line}`, background: '#fff', borderRadius: 12, width: 50, display: 'grid', placeItems: 'center', cursor: 'pointer', color: C.ink2, flexShrink: 0 }}>
          <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {acik ? (
        <div role="listbox"
          style={{ position: 'absolute', top: 'calc(100% + 8px)', left: 0, right: 0, zIndex: 30, background: 'rgba(255,255,255,.98)', backdropFilter: 'blur(12px)',
            border: `1px solid ${C.line}`, borderRadius: 14, boxShadow: '0 12px 40px rgba(17,24,39,.14), 0 2px 6px rgba(17,24,39,.06)', padding: 6 }}>
          {ARALIKLAR.filter((a) => a.key !== 'ozel').map((a) => {
            const on = araKey === a.key;
            return (
              <button key={a.key} type="button" role="option" aria-selected={on}
                onClick={() => { secAralik(a.key); setAcik(false); }}
                onMouseEnter={(e) => { if (!on) e.currentTarget.style.background = '#F3F4F8'; }}
                onMouseLeave={(e) => { if (!on) e.currentTarget.style.background = 'transparent'; }}
                style={{ width: '100%', border: 0, cursor: 'pointer', borderRadius: 9, padding: '8px 10px', fontFamily: FONT, textAlign: 'left',
                  display: 'flex', alignItems: 'center', gap: 8, background: on ? C.accentSoft : 'transparent' }}>
                <span style={{ width: 14, color: C.accent, display: 'grid', placeItems: 'center' }}>{on ? <Check size={14} strokeWidth={2.5} /> : null}</span>
                <span style={{ flex: 1, fontSize: 13.5, fontWeight: on ? 600 : 500, color: on ? C.accent : C.ink, whiteSpace: 'nowrap' }}>{a.label}</span>
                <span style={{ fontSize: 11.5, color: C.faint, whiteSpace: 'nowrap' }} className="tabular-nums">{kisaAralik(aralikOf(a.key))}</span>
              </button>
            );
          })}
          <div style={{ height: 1, background: C.hair, margin: '6px 4px' }} />
          <div style={{ padding: '6px 10px 8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: araKey === 'ozel' ? 600 : 500, color: araKey === 'ozel' ? C.accent : C.ink }}>
              <span style={{ width: 14, display: 'grid', placeItems: 'center' }}>{araKey === 'ozel' ? <Check size={14} strokeWidth={2.5} /> : null}</span>
              Custom range
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 6, marginTop: 8 }}>
              <input type="date" aria-label="Start" value={ozel[0]} min="2025-01-01" max={ozel[1]} onChange={(e) => setOzel([e.target.value, ozel[1]])} style={tarihInput} />
              <input type="date" aria-label="End" value={ozel[1]} min={ozel[0]} max={dun} onChange={(e) => setOzel([ozel[0], e.target.value])} style={tarihInput} />
            </div>
            <button type="button" onClick={() => { secAralik('ozel'); uygulaOzel(); setAcik(false); }}
              style={{ width: '100%', marginTop: 8, border: 0, background: C.accent, color: '#fff', borderRadius: 9, padding: '8px 12px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
              Apply
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------- PDF */

/**
 * PDF: sayfanın o anki görünümü gizli bir iframe'e kopyalanır ve orada yazdırılır (tarayıcının
 * "PDF olarak kaydet"i). Metin vektör kalır, tablo başlıkları her sayfada tekrarlanır.
 * Baskıda medya sorguları kağıt genişliğine göre çalışır → ızgaralar .pdf-* ile sabitlenir.
 * zoom 0.66: ~1560px düzen yatay A4'ün yazdırılabilir alanına sığar.
 */
const PDF_CSS = `
@page { size: A4 landscape; margin: 9mm 8mm; }
html, body { margin: 0; background: ${C.ground}; }
body { zoom: 0.66; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.pdf-kok { width: 1560px; padding: 8px 4px; font-family: ${FONT}; color: ${C.ink}; }
.pdf-gizle { display: none !important; }
.pdf-goster { display: block !important; }
.pdf-kpi { grid-template-columns: repeat(6, minmax(0, 1fr)) !important; }
.overflow-auto, .overflow-x-auto { max-height: none !important; overflow: visible !important; opacity: 1 !important; }
table { min-width: 0 !important; }
thead { display: table-header-group; }
th, td, thead { position: static !important; }
tr, .pdf-blok { break-inside: avoid; page-break-inside: avoid; }
h1, h2, .pdf-bolum { break-after: avoid; page-break-after: avoid; }
* { box-shadow: none !important; transition: none !important; animation: none !important; }
`;

function pdfIndir(kaynak, baslik) {
  if (!kaynak) return;
  const eski = document.getElementById('zoo-en-pdf-iframe');
  if (eski) eski.remove();
  const iframe = document.createElement('iframe');
  iframe.id = 'zoo-en-pdf-iframe';
  iframe.setAttribute('aria-hidden', 'true');
  Object.assign(iframe.style, { position: 'fixed', right: '0', bottom: '0', width: '1600px', height: '900px', border: '0', opacity: '0', pointerEvents: 'none', zIndex: '-1' });
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument;
  const stiller = [...document.querySelectorAll('style, link[rel="stylesheet"]')].map((n) => n.outerHTML).join('\n');
  const kok = kaynak.cloneNode(true);
  kok.removeAttribute('style');
  kok.className = 'pdf-kok';
  const olusturma = new Date().toLocaleString('en-US', { dateStyle: 'long', timeStyle: 'short' });
  doc.open();
  doc.write(`<!doctype html><html lang="en"><head><meta charset="utf-8"><title>${baslik.replace(/</g, '')}</title>${stiller}<style>${PDF_CSS}</style></head>
    <body>${kok.outerHTML}<div style="font: 12px ${FONT}; color: ${C.faint}; padding: 12px 4px 0;">Glowytics · generated on ${olusturma}</div></body></html>`);
  doc.close();

  let basildi = false;
  const yazdir = () => {
    if (basildi) return;
    basildi = true;
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } finally {
      setTimeout(() => iframe.remove(), 1000);
    }
  };
  const linkler = [...doc.querySelectorAll('link[rel="stylesheet"]')];
  if (!linkler.length) { setTimeout(yazdir, 150); return; }
  let kalan = linkler.length;
  const bitti = () => { kalan -= 1; if (kalan <= 0) setTimeout(yazdir, 150); };
  linkler.forEach((l) => { l.addEventListener('load', bitti); l.addEventListener('error', bitti); });
  setTimeout(yazdir, 3000);
}

/* ---------------------------------------------------------------- sayfa */

export default function HupalupaZooFamilyReportEn() {
  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
  const headers = useMemo(
    () => ({ 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) }),
    [brandId]
  );

  const [araKey, setAraKey] = useState('buyil');
  const [range, setRange] = useState(() => aralikOf('buyil'));
  const [ozel, setOzel] = useState(() => aralikOf('buyil'));
  const [refreshTick, setRefreshTick] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const raporRef = useRef(null);

  const secAralik = (k) => {
    setAraKey(k);
    if (k === 'ozel') return;
    const r = aralikOf(k);
    setRange(r); setOzel(r);
  };
  const uygulaOzel = () => setRange(ozel);

  const getir = useCallback(async (r, refresh) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/hupalupa/zoo-family?startDate=${r[0]}&endDate=${r[1]}${refresh ? '&refresh=1' : ''}`, { credentials: 'include', headers });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Report could not be loaded');
      setData(json);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    if (brandId) getir(range, refreshTick > 0);
  }, [brandId, range, getir, refreshTick]);

  const t = turet(data?.totals || { cost: 0, impressions: 0, clicks: 0, conv: 0, value: 0 });
  const p = data?.platforms || {};
  const gm = turet(topla([p.google || {}, p.meta || {}]));
  const ga4 = data?.ga4;
  const ek = data?.products?.extra;
  const ekHarcama = (ek?.google?.cost || 0) + (ek?.meta?.cost || 0);
  const aktifPlatform = PLATFORMLAR.filter((k) => p[k]?.cost > 0);

  return (
    <div style={{ background: C.ground, minHeight: '100%', fontFamily: FONT, color: C.ink }} className="px-4 sm:px-8 py-8">
      <div ref={raporRef} style={{ maxWidth: 1640, margin: '0 auto' }}>
        {/* Başlık */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.accent }}>HUPALUPA Store</div>
            <h1 style={{ fontSize: 44, fontWeight: 700, letterSpacing: '-0.03em', margin: '4px 0 0', lineHeight: 1.05 }}>The Zoo Family advertising report</h1>
            <div style={{ fontSize: 17, color: C.muted, marginTop: 10, lineHeight: 1.45 }}>
              {aralikEtiket(range[0], range[1])} · Google, Meta and TikTok ads featuring Zoo Family products
            </div>
          </div>
          <div className="pdf-gizle" style={{ display: 'flex', gap: 8, width: 420, maxWidth: '100%', position: 'relative', zIndex: 20 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <TarihSecici araKey={araKey} secAralik={secAralik} ozel={ozel} setOzel={setOzel} uygulaOzel={uygulaOzel}
                range={range} loading={loading} onYenile={() => setRefreshTick((x) => x + 1)} />
            </div>
            <button type="button" onClick={() => pdfIndir(raporRef.current, `HUPALUPA Store - The Zoo Family Report ${aralikEtiket(range[0], range[1])}`)}
              disabled={loading || !data} title={loading ? 'Loading data…' : 'Save page as PDF'}
              style={{ display: 'flex', alignItems: 'center', gap: 7, border: 0, borderRadius: 12, padding: '0 16px', background: C.ink, color: '#fff',
                fontSize: 13.5, fontWeight: 600, fontFamily: FONT, cursor: loading ? 'wait' : 'pointer', opacity: loading || !data ? 0.5 : 1, flexShrink: 0 }}>
              <FileDown size={16} /> PDF
            </button>
          </div>
        </div>

        {/* Özet */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 pdf-kpi" style={{ gap: 14, marginTop: 28, opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }}>
          <Kpi label="Spend" value={tlShort(t.cost)}
            lines={aktifPlatform.map((k) => [PLATFORM[k].label, tl(p[k].cost)])} />
          <Kpi label="Impressions" value={sayiKisa(t.impressions)}
            lines={[['CPM', t.impressions ? tl2((t.cost / t.impressions) * 1000) : '—'], ['Total', nf(t.impressions)]]} />
          <Kpi label="Clicks" value={sayiKisa(t.clicks)}
            lines={[['Google + Meta', nf(gm.clicks)], p.tiktok?.clicks ? ['TikTok', nf(p.tiktok.clicks)] : null]} />
          <Kpi label="CTR (Google + Meta)" value={ctrYazi(gm.ctr)}
            lines={[['Google', ctrYazi(oran(p.google?.clicks, p.google?.impressions))], ['Meta', ctrYazi(oran(p.meta?.clicks, p.meta?.impressions))]]} />
          <Kpi label="Conversions (purchases)" value={nf1(t.conv)}
            lines={[['Conv. value', tl(t.value)], ['Cost per conversion', t.conv ? tl(t.cost / t.conv) : '—']]} />
          <Kpi label="ROAS" value={roasYazi(t.roas)} renk={C.accent}
            lines={[['Google', roasYazi(oran(p.google?.value, p.google?.cost))], ['Meta', roasYazi(oran(p.meta?.value, p.meta?.cost))]]} />
        </div>

        {/* Site satışı + genel kampanyalar şeridi */}
        <Card style={{ marginTop: 14, padding: '18px 24px', opacity: loading ? 0.55 : 1 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px 48px', alignItems: 'baseline' }} className="tabular-nums">
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.muted }}>Zoo Family sales on site (GA4)</div>
              <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4 }}>{ga4 ? tl(ga4.revenue) : '—'} <span style={{ fontSize: 15, fontWeight: 500, color: C.muted }}>{ga4 ? `· ${nf(ga4.qty)} units` : ''}</span></div>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.muted }}>Ad spend / site sales</div>
              <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4 }}>{ga4?.revenue ? pct(t.cost / ga4.revenue, 1) : '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.muted }}>Additional spend in general catalog campaigns</div>
              <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4 }}>{tl(ekHarcama)}</div>
            </div>
            <div style={{ flex: '1 1 320px', fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
              Site sales are total Zoo Family revenue from any channel; they are not ad-attributed.
              Additional spend is the share Zoo Family products received in "All Products" catalog campaigns and is not included in the total above.
            </div>
          </div>
          {data?.ga4Error ? <div style={{ fontSize: 12.5, color: C.down, marginTop: 8 }}>GA4 unavailable: {data.ga4Error}</div> : null}
        </Card>

        <BolumBaslik eyebrow="Platforms" title="Google, Meta and TikTok"
          sub="Platform totals of campaigns, ad groups and ad sets dedicated to Zoo Family." />
        <PlatformTablosu data={data} />

        <BolumBaslik eyebrow="Monthly" title="Spend and results by month"
          sub="Monthly spend by platform, alongside the site's Zoo Family revenue for that month." />
        <AylikTablo data={data} loading={loading} />

        <BolumBaslik eyebrow="Campaigns" title="Campaign performance"
          sub="Every ad group, PMax asset group and ad set dedicated to Zoo Family. Click a column header to sort." />
        <KampanyaTablosu data={data} loading={loading} />

        <BolumBaslik eyebrow="Products" title="Zoo Family products shown in ads"
          sub="Product-level spend in Google Shopping and Meta catalog ads, including the share from general catalog campaigns." />
        <UrunTablosu data={data} loading={loading} />

        <BolumBaslik eyebrow="Sales" title="Best-selling Zoo Family products on site"
          sub="GA4 e-commerce: views, add to cart, units and revenue." />
        <SatisTablosu data={data} loading={loading} />

        <BolumBaslik eyebrow="Product range" title="Zoo Family products at HUPALUPA Store"
          sub="How many products we carry in each category, current price and stock, and what each one sold on the site." />
        <Urunler />

        <BolumBaslik eyebrow="Market" title="Zoo Family next to alternatives on the Turkish market"
          sub="Similar products and their current prices, side by side with ours. Snapshot taken October 27, 2026." />
        <Muadiller />

        <BolumBaslik eyebrow="Creative" title="Ad creatives we used for Zoo Family"
          sub="All images and videos that ran in Meta and Google campaigns, with the results of each Meta ad. Snapshot taken October 27, 2026." />
        <Assetler />

        <BolumBaslik eyebrow="Search demand" title="How often Zoo Family and alternatives are searched in Turkey"
          sub="Monthly Google searches for the brand, the kids camera category, instant-print and kids camera competitors, night lights and refill paper. Fixed snapshot." />
        <AramaHacmi />

        <BolumBaslik eyebrow="Brand search in Europe" title="How often “The Zoo Family” is searched across Europe"
          sub="Monthly Google searches for the brand name in 29 European countries, with Turkey as reference and Hoppstar and Kidizoom for scale. Fixed snapshot." />
        <AvrupaArama />
        <AvrupaGenel />

        <div style={{ fontSize: 12, color: C.faint, lineHeight: 1.6, marginTop: 28, paddingBottom: 24 }}>
          Spend, impressions, clicks and conversions come from the Google Ads, Meta and TikTok accounts; site sales from GA4. All amounts in Turkish lira (TRY).
          TikTok data is recorded since September 1, 2026 and the Meta product breakdown since June 9, 2026.
          {data?.cached ? ' · cached' : ''}
        </div>
      </div>
    </div>
  );
}
